import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Alert,
  Divider,
  styled,
  SelectChangeEvent,
  InputAdornment,
  FormHelperText,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  DialogContentText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../contexts/AuthContext';
import { CategoryService, BrandService, WarehouseService, InventoryService } from './services';
import { accountingApi } from '../../../config/apiClient';
import type { BulkCreateItemDto } from './services/inventoryService';
import type { CreateInventoryItemDto } from './services/inventoryService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { UNIT_OPTIONS } from '../../../constants/units';

// Arayüz tanımlamaları
interface InventoryImportModalProps {
  open: boolean;
  onClose: () => void;
}

interface ColumnMapping {
  name: string; // Excel'deki sütun adı (A, B, C)
  fieldName: string; // Veritabanındaki alan adı (name, barcode, stockCode)
  label: string; // Kullanıcı arayüzünde gösterilen alan adı (Ürün Adı, Barkod, Stok Kodu)
  required: boolean; // Zorunlu alan mı?
  selected: string; // Kullanıcının seçtiği sütun
  showColumnSelect: boolean; // Sütun seçimi gösterilsin mi?
  customValue?: string | null; // Özel değer (para birimi, depo vb. için)
}

interface PreviewItem {
  [key: string]: any;
}

/**
 * Ana → Alt → Alt 2 kategori zincirini yukarıdan aşağı BİTİŞİK okur.
 * Ana kategori boşsa alt seviyeler yok sayılır (hiyerarşi köksüz kurulamaz);
 * alt boş ama alt-2 doluysa alt-2 de yok sayılır.
 */
const buildCategoryChain = (main?: any, sub1?: any, sub2?: any): string[] => {
  const chain: string[] = [];
  const m = main?.toString().trim() || '';
  if (!m) return chain;
  chain.push(m);
  const s1 = sub1?.toString().trim() || '';
  if (!s1) return chain;
  chain.push(s1);
  const s2 = sub2?.toString().trim() || '';
  if (s2) chain.push(s2);
  return chain;
};

/**
 * Resim URL hücresini okur. Görünen metin http(s) ile başlıyorsa aynen döner; boşsa veya
 * URL değilse hücrenin köprü (hyperlink) hedefine bakılır. Excel'de URL'ler sık sık görünen
 * metin yerine köprü olarak saklanır (köprülü hücre veya =HYPERLINK formülü) — o durumda
 * sheet_to_json değeri boş/farklı olur ve URL yalnızca cell.l.Target içinde bulunur.
 */
const resolveImageCellValue = (
  worksheet: XLSX.WorkSheet,
  col: string,
  row: Record<string, any>
): string => {
  const raw = row[col];
  const text = raw !== undefined && raw !== null ? String(raw).trim() : '';
  if (/^https?:\/\//i.test(text)) return text;
  
  // sheet_to_json her satıra 0 tabanlı __rowNum__ ekler → hücre adresi hesaplanır
  const rowNum = (row as any).__rowNum__;
  if (typeof rowNum === 'number') {
    const cell: any = (worksheet as any)[`${col}${rowNum + 1}`];
    const target = cell?.l?.Target ? String(cell.l.Target).trim() : '';
    if (target) return target;
  }
  return text;
};

/** İsim + üst kategoriye göre eşleştirme — aynı isim farklı üstler altında ayrı kategoridir. */
const findCategoryInList = (
  list: Array<{ id: string; name: string; parentId: string | null }>,
  name: string,
  parentId: string | null
) =>
  list.find(
    (c) => c.name.toLowerCase() === name.toLowerCase() && (c.parentId ?? null) === parentId
  );

interface Warehouse {
  id: string;
  name: string;
  address?: string;
  description?: string;
  isDefault?: boolean;
}

// Para birimi arayüzü
interface Currency {
  code: string;
  active: boolean;
}

// Birim seçenekleri listesi
const unitOptions = [...UNIT_OPTIONS];

// Kategori arayüzü
interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

// Marka arayüzü
interface Brand {
  id: string;
  name: string;
  description?: string;
  website?: string;
  createdAt?: string;
}

// Çakışan barkod bilgisi için interface ekleyelim
interface BarcodeConflict {
  barcode: string;
  importItem: PreviewItem; // İçe aktarılmak istenen ürün
  existingItem: {
    id: string;
    name: string;
    stockCode: string;
    categoryName?: string;
    warehouseName?: string;
    brandName?: string;
    quantity?: number;
    unit?: string;
  }; // Veritabanında var olan ürün
}

// Stil komponentleri
const ImportArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  maxHeight: '400px',
  margin: theme.spacing(2, 0),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#555',
  },
}));

// Excel sütun adlarını oluşturan yardımcı fonksiyon
const generateExcelColumns = (count: number = 50): string[] => {
  const columns: string[] = [];
  
  for (let i = 0; i < count; i++) {
    let columnName = '';
    let num = i;
    
    while (num >= 0) {
      columnName = String.fromCharCode(65 + (num % 26)) + columnName;
      num = Math.floor(num / 26) - 1;
    }
    
    columns.push(columnName);
  }
  
  return columns;
};

// Varsayılan para birimleri - sadece veritabanından hiç para birimi çekilemezse kullanılacak
const DEFAULT_CURRENCIES: { code: string, active: boolean }[] = [
  { code: 'TRY', active: true }
];

// Function type tanımlaması
type RealtimeListenerToggleFunction = (active: boolean) => void;

// Fonksiyon referansını tutan değişken
let _toggleRealtimeListener: RealtimeListenerToggleFunction | null = null;

// Dışarıdan çağrılabilecek fonksiyon
const setRealtimeListenerToggle = (fn: RealtimeListenerToggleFunction) => {
  _toggleRealtimeListener = fn;
};

// Dışarıdan erişilebilen wrapper fonksiyon
const toggleRealtimeListener = (active: boolean) => {
  if (_toggleRealtimeListener) {
    _toggleRealtimeListener(active);
  } else {
    console.log('Realtime listener toggle fonksiyonu henüz atanmamış');
  }
};

const InventoryImportModal: React.FC<InventoryImportModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation('accounting');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { warehouses, userDetails } = useAuth();
  
  // State tanımlamaları
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [startRow, setStartRow] = useState<number>(2); // Varsayılan olarak 2. satırdan başla (ilk satır başlık)
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [mappingValid, setMappingValid] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1); // 1: Eşleştirme, 2: Önizleme, 3: Sonuç
  const [savingData, setSavingData] = useState<boolean>(false);
  const [requiredFieldsMissing, setRequiredFieldsMissing] = useState<boolean>(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([
    { code: 'TRY', active: true } // Varsayılan olarak TRY her zaman olacak
  ]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  // İşlemi iptal etmek için flag
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  
  // Kategori ve marka yükleme durumu takibi için state
  const [existingCategories, setExistingCategories] = useState<InventoryCategory[]>([]);
  const [existingBrands, setExistingBrands] = useState<Brand[]>([]);
  // Barkod çakışmaları ve gösterim durumu
  const [barcodeConflicts, setBarcodeConflicts] = useState<BarcodeConflict[]>([]);
  const [showBarcodeConflicts, setShowBarcodeConflicts] = useState<boolean>(false);
  const [canBeNegative, setCanBeNegative] = useState<boolean>(true); // YENİ: Stok eksiye düşebilir ayarı
  
  // ✅ YENİ: Başarısız resim ve ürün logları
  const [failedImages, setFailedImages] = useState<Array<{ productName: string, imageUrl: string, error: string }>>([]);
  const [failedProducts, setFailedProducts] = useState<Array<{ name: string, stockCode: string, barcode: string, error: string }>>([]);
  
  // ✅ YENİ: Önizleme doğrulama sonuçları (yüklemeden önce hızlı tarama)
  const [validationResult, setValidationResult] = useState<{
    loading: boolean;
    totalItems: number;
    validItems: number;
    invalidItems: number;
    dbConflicts: Array<{ 
      name: string, 
      stockCode: string, 
      barcode: string, 
      reason: string,
      // ✅ YENİ: Excel'deki ve mevcut stoktaki detaylı bilgiler
      excelItem: {
        row: number;
        name: string;
        stockCode: string;
        barcode: string;
        salePrice: number;
        purchasePrice: number;
        quantity: number;
        unit: string;
        categoryName: string;
        brandName: string;
      };
      existingItem: {
        id: string;
        name: string;
        stockCode: string;
        barcode: string;
        salePrice: number;
        purchasePrice: number;
        quantity: number;
        unit: string;
        categoryName: string;
        brandName: string;
      };
    }>;
    excelDuplicates: Array<{ 
      name: string; 
      stockCode: string; 
      barcode: string; 
      duplicateOf: string; 
      row: number;
      // ✅ YENİ: Orijinal ürün bilgileri (alt alta karşılaştırma için)
      originalItem: {
        row: number;
        name: string;
        stockCode: string;
        barcode: string;
        salePrice: number;
        purchasePrice: number;
        quantity: number;
        unit: string;
        categoryName: string;
        brandName: string;
      };
      duplicateItem: {
        row: number;
        name: string;
        stockCode: string;
        barcode: string;
        salePrice: number;
        purchasePrice: number;
        quantity: number;
        unit: string;
        categoryName: string;
        brandName: string;
      };
    }>;
    emptyRequired: Array<{ row: number, field: string }>;
  } | null>(null);
  
  // Kaydetme durumu
  const [savingStatus, setSavingStatus] = useState<{
    categories: { total: number, saved: number, loading: boolean },
    brands: { total: number, saved: number, loading: boolean },
    images: { total: number, saved: number, failed: number, loading: boolean }, // YENİ: Resim işleme durumu
    inventory: { 
      total: number, 
      saved: number, 
      failed: number, // Başarısız ürün sayısı
      loading: boolean 
    }
  }>({
    categories: { total: 0, saved: 0, loading: false },
    brands: { total: 0, saved: 0, loading: false },
    images: { total: 0, saved: 0, failed: 0, loading: false }, // YENİ
    inventory: { 
      total: 0, 
      saved: 0, 
      failed: 0, // Başarısız ürün sayısı eklendi
      loading: false 
    }
  });
  
  // Excel sütun adları
  const excelColumns = generateExcelColumns();
  
  // Sütun eşleştirmeleri
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([
    { fieldName: 'name', label: 'Ürün Adı', name: '', required: true, selected: '', showColumnSelect: true },
    { fieldName: 'barcode', label: 'Barkod', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'stockCode', label: 'Stok Kodu', name: '', required: false, selected: '', showColumnSelect: true },
    // Kategori hiyerarşisi: sistem 3 seviye destekler (ana → alt → alt-2).
    // Ürün, zincirin en derin dolu seviyesine bağlanır; eksik kategoriler hiyerarşiyle oluşturulur.
    { fieldName: 'categoryMain', label: 'Ana Kategori', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'categorySub1', label: 'Alt Kategori', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'categorySub2', label: 'Alt Kategori 2 (En Alt)', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'brandId', label: 'Marka', name: '', required: false, selected: '', showColumnSelect: true },
    // ⚠️ TODO: quantity ve warehouseId backend'de WarehouseStock ile yönetilecek
    { fieldName: 'quantity', label: 'Miktar', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'unit', label: 'Birim', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'purchasePrice', label: 'Alış Fiyatı', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'purchaseCurrency', label: 'Alış Para Birimi', name: '', required: false, selected: '', showColumnSelect: false, customValue: 'TRY' },
    { fieldName: 'salePrice', label: 'Satış Fiyatı', name: '', required: true, selected: '', showColumnSelect: true },
    { fieldName: 'saleCurrency', label: 'Satış Para Birimi', name: '', required: false, selected: '', showColumnSelect: false, customValue: 'TRY' },
    { fieldName: 'vatRate', label: 'KDV Oranı', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'vatStatus', label: 'KDV Durumu', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'sctRate', label: 'ÖTV Oranı', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'sctStatus', label: 'ÖTV Durumu', name: '', required: false, selected: '', showColumnSelect: false, customValue: null },
    { fieldName: 'description', label: 'Açıklama', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'imageUrl', label: 'Resim URL', name: '', required: false, selected: '', showColumnSelect: true },
    // ✅ warehouseId artık otomatik olarak Merkez Depo'ya atanıyor (UI'da gösterilmiyor)
    { fieldName: 'warehouseId', label: 'Depo (Otomatik: Merkez Depo)', name: '', required: false, selected: '', showColumnSelect: false, customValue: null },
    { fieldName: 'isEcommerce', label: 'E-Ticaret', name: '', required: false, selected: '', showColumnSelect: true },
    { fieldName: 'departmentNo', label: 'Departman No (ÖKC)', name: '', required: false, selected: '', showColumnSelect: true },
  ]);
  
  // Zorunlu alanların kontrolü
  useEffect(() => {
    const requiredMappings = columnMappings.filter(mapping => mapping.required);
    const allRequiredSelected = requiredMappings.every(mapping => 
      (mapping.showColumnSelect && mapping.selected !== '') || 
      (!mapping.showColumnSelect && mapping.customValue !== null)
    );
    setRequiredFieldsMissing(!allRequiredSelected);
  }, [columnMappings]);
  
  // Kullanıcı oturumunu, şirket ID'sini ve depoları kontrol et
  useEffect(() => {
    if (!open) return;

    const cId = userDetails?.companyId;
    if (!cId) {
      setError('Kullanıcı bilgileri bulunamadı.');
      return;
    }

    setCompanyId(cId);
    // Para birimlerini yükle (eğer varsa)
    loadCurrencies(cId);
    // Kategorileri ve markaları yükle
    loadCategories(cId);
    loadBrands(cId);
  }, [open, userDetails]);
  
  // ✅ Merkez Depo kontrolü (warehouses yüklendiğinde)
  useEffect(() => {
    if (warehouses.length > 0) {
      // ✅ Merkez Depo'yu bul
      const centralWarehouse = warehouses.find(w => 
        w.name.toLowerCase().includes('merkez') ||
        w.name.toLowerCase() === 'merkez depo' ||
        w.name.toLowerCase() === 'ana depo'
      );
      
      if (!centralWarehouse) {
        // ❌ Merkez Depo bulunamadı - Kullanıcıyı uyar
        setError('Merkez Depo bulunamadı! Lütfen önce "Merkez Depo" adında bir depo oluşturun veya Depo sayfasına bir kere girin.');
        return;
      }
      
      // ✅ Merkez Depo bulundu - Otomatik olarak ata
      console.log('✅ [IMPORT] Merkez Depo bulundu:', centralWarehouse.name);
      setColumnMappings(prev => prev.map(mapping => 
        mapping.fieldName === 'warehouseId' 
          ? { ...mapping, customValue: centralWarehouse.id } 
          : mapping
      ));
    }
  }, [warehouses]);
  
  // Para birimlerini yükleme (SQL API)
  const loadCurrencies = async (companyId: string) => {
    try {
      const response = await accountingApi.get(
        `/api/currency/settings`
      );
      const result = response.data;
      
      // Varsayılan para birimi TRY'yi hazırla
      const currenciesList: Currency[] = [
        { code: 'TRY', active: true } // TRY her zaman aktif olacak
      ];
      
      if (result.success && result.data) {
        // API'den gelen para birimleri: { TRY: true, USD: true, EUR: false }
        Object.entries(result.data).forEach(([code, active]: [string, any]) => {
          // TRY zaten eklendiği için tekrar eklemeye gerek yok
          if (code !== 'TRY' && active === true) {
            currenciesList.push({ code, active: true });
          }
        });
      }
      
      setCurrencies(currenciesList);
      
      // Para birimi eşleştirmelerini güncelle
      setColumnMappings(prev => prev.map(mapping => 
        (mapping.fieldName === 'purchaseCurrency' || mapping.fieldName === 'saleCurrency') 
          ? { ...mapping, customValue: 'TRY' } 
          : mapping
      ));
    } catch (error) {
      console.error('Para birimleri yüklenirken hata:', error);
      // Hata durumunda varsayılan para birimlerini kullan
      setCurrencies([{ code: 'TRY', active: true }]);
    }
  };
  
  // Kategorileri yükleme (SQL API) — taze listeyi döndürür (stale closure'ı önlemek için)
  const loadCategories = async (companyId: string): Promise<InventoryCategory[]> => {
    try {
      const categoriesList = await CategoryService.getAllCategories(companyId);
      setExistingCategories(categoriesList);
      return categoriesList;
    } catch (error) {
      console.error('Kategori verileri yüklenirken hata:', error);
      setExistingCategories([]);
      return [];
    }
  };
  
  // Markaları yükleme (SQL API) — taze listeyi döndürür (stale closure'ı önlemek için)
  const loadBrands = async (companyId: string): Promise<any[]> => {
    try {
      const brandsList = await BrandService.getAllBrands(companyId);
      setExistingBrands(brandsList);
      return brandsList;
    } catch (error) {
      console.error('Marka verileri yüklenirken hata:', error);
      setExistingBrands([]);
      return [];
    }
  };
  
  // Sütun eşleştirmesini güncelle
  const handleColumnChange = (index: number, newValue: string) => {
    const updatedMappings = [...columnMappings];
    updatedMappings[index].selected = newValue;
    setColumnMappings(updatedMappings);
    
    // Eşleştirmenin geçerliliğini kontrol et
    validateMappings(updatedMappings);
  };
  
  // Özel değer güncelleme (para birimi, depo, vb.)
  const handleCustomValueChange = (index: number, newValue: string) => {
    const updatedMappings = [...columnMappings];
    updatedMappings[index].customValue = newValue;
    setColumnMappings(updatedMappings);
    
    // Geçerliliği kontrol et
    validateMappings(updatedMappings);
  };
  
  // Eşleştirme geçerliliğini kontrol et
  const validateMappings = (mappings: ColumnMapping[]) => {
    // Excel sütunlarının eşleşme geçerliliğini kontrol et
    const selectedColumns = mappings
      .filter(mapping => mapping.showColumnSelect && mapping.selected !== '')
      .map(mapping => mapping.selected);
    
    // Tüm seçilen sütunlar benzersiz mi?
    const uniqueColumns = new Set(selectedColumns);
    const isValid = uniqueColumns.size === selectedColumns.length;
    
    setMappingValid(isValid);
  };
  
  // Sütun giriş değeri değiştiğinde
  const handleColumnInputChange = (index: number, newValue: string) => {
    // Sadece büyük harfler kabul et, sayı ve diğer karakterleri filtrele
    const onlyLetters = newValue.replace(/[^A-Za-z]/g, '');
    const upperValue = onlyLetters.toUpperCase();
    
    const updatedMappings = [...columnMappings];
    updatedMappings[index].selected = upperValue;
    setColumnMappings(updatedMappings);
    
    // Geçerliliği kontrol et
    validateMappings(updatedMappings);
  };
  
  // Başlangıç satırını güncelle
  const handleStartRowChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value > 0) {
      setStartRow(value);
    }
  };
  
  // Excel dosyasını seçme işlemi
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      
      // Dosyayı oku ve sheet isimleri al
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheets = workbook.SheetNames;
        setSheetNames(sheets);
        
        if (sheets.length > 0) {
          setSelectedSheet(sheets[0]);
        }
      };
      
      reader.readAsArrayBuffer(selectedFile);
    }
  };
  
  // Excel dosyasını seçme alanına tıklama
  const handleClickImport = () => {
    const input = document.getElementById('excel-file-input');
    if (input) {
      input.click();
    }
  };
  
  // Sheet seçimini güncelle
  const handleSheetChange = (event: SelectChangeEvent) => {
    setSelectedSheet(event.target.value);
  };
  
  // Minimum sütun sayısı ayarı
  const [minRequiredColumns, setMinRequiredColumns] = useState<number>(2);
  const [enableEmptyRowFilter, setEnableEmptyRowFilter] = useState<boolean>(true);
  const [skippedRows, setSkippedRows] = useState<number>(0);
  
  // Bir satırdaki dolu sütun sayısını kontrol eden fonksiyon
  const countFilledColumns = (row: Record<string, any>): number => {
    // Boş olmayan değerlerin sayısını say
    let filledCount = 0;
    
    // Excel'den gelen satır verilerindeki her bir hücreyi kontrol et
    Object.values(row).forEach(value => {
      // undefined, null, boş string veya sadece boşluk karakterleri içermeyen değerler dolu kabul edilir
      if (value !== undefined && value !== null && value.toString().trim() !== '') {
        filledCount++;
      }
    });
    
    return filledCount;
  };
  
  // Verileri önizleme ve doğrulama
  const handlePreview = async () => {
    if (!file || !selectedSheet) {
      setError('Lütfen bir Excel dosyası ve çalışma sayfası seçin.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setValidationResult({ 
      loading: true, 
      totalItems: 0, 
      validItems: 0, 
      invalidItems: 0, 
      dbConflicts: [], 
      excelDuplicates: [], 
      emptyRequired: [] 
    });
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Seçilen sheet'i al
        const worksheet = workbook.Sheets[selectedSheet];
        
        // JSON'a çevir - TÜM verileri al
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 'A', range: startRow - 1 });
        
        // Önizleme verileri (ilk 10 satır)
        const preview: PreviewItem[] = [];
        let skippedRowCount = 0;
        
        // ✅ TÜM verileri işle (doğrulama için)
        const allItems: Array<{ 
          row: number; 
          name: string; 
          stockCode: string; 
          barcode: string;
          salePrice: number;
          purchasePrice: number;
          quantity: number;
          unit: string;
          /** "Ana > Alt > Alt2" biçiminde okunur kategori yolu (görsel karşılaştırmalar için) */
          categoryPath: string;
          brandId: string;
        }> = [];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i] as Record<string, any>;
          
          // Boş satır filtresi
          if (enableEmptyRowFilter && countFilledColumns(row) < minRequiredColumns) {
            if (i < 10) skippedRowCount++;
            continue;
          }
          
          const previewItem: PreviewItem = {};
          let hasSctRate = false;
          
          // Eşleştirmeleri kullan
          columnMappings.forEach(mapping => {
            if (mapping.showColumnSelect && mapping.selected) {
              const cellValue = row[mapping.selected];
              previewItem[mapping.fieldName] = cellValue !== undefined ? cellValue : '';
              
              if (mapping.fieldName === 'sctRate' && cellValue) {
                const numValue = typeof cellValue === 'number' ? cellValue : parseFloat(cellValue) || 0;
                if (numValue > 0) hasSctRate = true;
              }
            } else if (!mapping.showColumnSelect && mapping.customValue !== null) {
              previewItem[mapping.fieldName] = mapping.customValue;
            }
          });
          
          if (hasSctRate) {
            previewItem['sctStatus'] = 'excluded';
          }
          
          // Resim URL: metin URL değilse hücrenin köprü (hyperlink) hedefinden oku
          const imgMappingRow = columnMappings.find(m => m.fieldName === 'imageUrl');
          if (imgMappingRow?.showColumnSelect && imgMappingRow.selected) {
            previewItem.imageUrl = resolveImageCellValue(worksheet, imgMappingRow.selected, row);
          }
          
          // İlk 10 satırı önizlemeye ekle
          if (preview.length < 10) {
            preview.push(previewItem);
          }
          
          // TÜM ürünleri doğrulama listesine ekle
          allItems.push({
            row: i + startRow, // Excel satır numarası
            name: previewItem.name?.toString() || '',
            stockCode: previewItem.stockCode?.toString() || '',
            barcode: previewItem.barcode?.toString() || '',
            salePrice: parseFloat(previewItem.salePrice?.toString() || '0') || 0,
            purchasePrice: parseFloat(previewItem.purchasePrice?.toString() || '0') || 0,
            quantity: parseFloat(previewItem.quantity?.toString() || '0') || 0,
            unit: previewItem.unit?.toString() || '',
            categoryPath: buildCategoryChain(
              previewItem.categoryMain,
              previewItem.categorySub1,
              previewItem.categorySub2
            ).join(' > '),
            brandId: previewItem.brandId?.toString() || ''
          });
        }
        
        setSkippedRows(skippedRowCount);
        setPreviewData(preview);

        // 🔎 [KATEGORİ TEŞHİS] Kategori/marka eşleştirme ve okuma teşhisi (3 seviyeli)
        const catMappings = ['categoryMain', 'categorySub1', 'categorySub2'].map(f => ({
          alan: f,
          excelSütunu: columnMappings.find(m => m.fieldName === f)?.selected || '(SEÇİLİ DEĞİL)',
        }));
        console.log('🏷️ [KATEGORİ TEŞHİS] Kategori sütun eşleştirmeleri:', catMappings);
        console.log('🏷️ [KATEGORİ TEŞHİS] Excel başlıkları:', jsonData.length > 0 ? Object.keys(jsonData[0] as Record<string, any>) : []);

        const rowsWithCategory = allItems.filter(it => it.categoryPath !== '');
        console.log(`🏷️ [KATEGORİ TEŞHİS] Kategori DOLU satır: ${rowsWithCategory.length}/${allItems.length}`);
        console.log('🏷️ [KATEGORİ TEŞHİS] İlk 5 satırın kategori yolu:',
          allItems.slice(0, 5).map(it => ({ satır: it.row, ürün: it.name, kategori: it.categoryPath || '(BOŞ)' })));

        const uniqueCatPaths = Array.from(new Set(rowsWithCategory.map(it => it.categoryPath)));
        console.log(`🏷️ [KATEGORİ TEŞHİS] Excel'deki benzersiz kategori yolu: ${uniqueCatPaths.length}`, uniqueCatPaths);
        console.log(`🏷️ [KATEGORİ TEŞHİS] Sistemdeki toplam kategori sayısı: ${existingCategories.length}`);
        if (uniqueCatPaths.length === 0) {
          console.warn('⚠️ [KATEGORİ TEŞHİS] Hiç kategori okunamadı! Olası sebep: Ana Kategori sütunu eşleştirilmemiş VEYA seçilen sütun yanlış/boş.');
        }

        // 🔎 [RESİM TEŞHİS] Resim URL sütunu okuma teşhisi (önizlemedeki ilk 10 satır)
        const imgMapping = columnMappings.find(m => m.fieldName === 'imageUrl');
        if (imgMapping?.selected) {
          const previewWithImage = preview.filter(p => p.imageUrl && String(p.imageUrl).trim() !== '');
          console.log(`🖼️ [RESİM TEŞHİS] Resim URL sütunu: ${imgMapping.selected} | önizlemede dolu: ${previewWithImage.length}/${preview.length}`);
          console.log('🖼️ [RESİM TEŞHİS] İlk 3 resim değeri:',
            preview.slice(0, 3).map(p => (p.imageUrl && String(p.imageUrl).trim()) || '(BOŞ)'));
          if (previewWithImage.length === 0) {
            console.warn('⚠️ [RESİM TEŞHİS] Hiç resim URL okunamadı! Seçilen sütunda metin veya köprü (hyperlink) yok. Sütun harfini ve hücre içeriklerini kontrol edin.');
          }
        }
        
        // ✅ DOĞRULAMA TARAMASI
        console.log('🔍 [VALIDATION] Hızlı doğrulama taraması başlıyor...');
        console.log(`📊 [VALIDATION] Toplam ürün: ${allItems.length}`);
        
        // Veritabanındaki mevcut ürünleri çek
        let existingItems: any[] = [];
        if (companyId) {
          try {
            existingItems = await InventoryService.getAllItemsSQL(companyId);
            console.log(`📊 [VALIDATION] Veritabanındaki ürün sayısı: ${existingItems.length}`);
          } catch (err) {
            console.error('Mevcut ürünler yüklenirken hata:', err);
          }
        }
        
        // Mevcut stok kodları ve barkodlar
        const existingStockCodes = new Set(existingItems.map(e => e.stockCode?.toLowerCase()).filter(Boolean));
        const existingBarcodes = new Set(existingItems.map(e => e.barcode?.toLowerCase()).filter(Boolean));
        
        // Excel içi duplikasyon kontrolü için Map'ler (tüm item bilgilerini tut)
        const excelStockCodes = new Map<string, { 
          row: number;
          name: string;
          stockCode: string;
          barcode: string;
          salePrice: number;
          purchasePrice: number;
          quantity: number;
          unit: string;
          categoryName: string;
          brandName: string;
        }>();
        const excelBarcodes = new Map<string, { 
          row: number;
          name: string;
          stockCode: string;
          barcode: string;
          salePrice: number;
          purchasePrice: number;
          quantity: number;
          unit: string;
          categoryName: string;
          brandName: string;
        }>();
        
        // Doğrulama sonuçları
        const dbConflicts: Array<{ 
          name: string, 
          stockCode: string, 
          barcode: string, 
          reason: string,
          excelItem: {
            row: number;
            name: string;
            stockCode: string;
            barcode: string;
            salePrice: number;
            purchasePrice: number;
            quantity: number;
            unit: string;
            categoryName: string;
            brandName: string;
          };
          existingItem: {
            id: string;
            name: string;
            stockCode: string;
            barcode: string;
            salePrice: number;
            purchasePrice: number;
            quantity: number;
            unit: string;
            categoryName: string;
            brandName: string;
          };
        }> = [];
        const excelDuplicates: Array<{ 
          name: string; 
          stockCode: string; 
          barcode: string; 
          duplicateOf: string; 
          row: number;
          originalItem: {
            row: number;
            name: string;
            stockCode: string;
            barcode: string;
            salePrice: number;
            purchasePrice: number;
            quantity: number;
            unit: string;
            categoryName: string;
            brandName: string;
          };
          duplicateItem: {
            row: number;
            name: string;
            stockCode: string;
            barcode: string;
            salePrice: number;
            purchasePrice: number;
            quantity: number;
            unit: string;
            categoryName: string;
            brandName: string;
          };
        }> = [];
        const emptyRequired: Array<{ row: number, field: string }> = [];
        
        let validCount = 0;
        
        for (const item of allItems) {
          let isValid = true;
          
          // 1. Zorunlu alan kontrolü (ürün adı ve satış fiyatı)
          if (!item.name || item.name.trim() === '') {
            emptyRequired.push({ row: item.row, field: 'Ürün Adı' });
            isValid = false;
          }
          
          // 2. Veritabanı çakışma kontrolü
          if (item.stockCode && existingStockCodes.has(item.stockCode.toLowerCase())) {
            const existing = existingItems.find(e => e.stockCode?.toLowerCase() === item.stockCode.toLowerCase());
            // Aynı ürün için birden fazla çakışma eklenmesini önle
            const alreadyAdded = dbConflicts.some(c => 
              c.excelItem.row === item.row && c.reason.includes('Stok kodu')
            );
            if (!alreadyAdded) {
              dbConflicts.push({
                name: item.name,
                stockCode: item.stockCode,
                barcode: item.barcode,
                reason: `Stok kodu veritabanında "${existing?.name || 'başka ürün'}" ile çakışıyor`,
                excelItem: {
                  row: item.row,
                  name: item.name || '',
                  stockCode: item.stockCode || '',
                  barcode: item.barcode || '',
                  salePrice: item.salePrice || 0,
                  purchasePrice: item.purchasePrice || 0,
                  quantity: item.quantity || 0,
                  unit: item.unit || '',
                  categoryName: item.categoryPath || '',
                  brandName: item.brandId?.toString() || ''
                },
                existingItem: {
                  id: existing?.id || '',
                  name: existing?.name || '',
                  stockCode: existing?.stockCode || '',
                  barcode: existing?.barcode || '',
                  salePrice: existing?.salePrice || 0,
                  purchasePrice: existing?.purchasePrice || 0,
                  quantity: existing?.warehouseStocks?.reduce((sum: number, ws: any) => sum + ws.quantity, 0) || 0,
                  unit: existing?.unit || '',
                  categoryName: existing?.categoryName || '',
                  brandName: existing?.brandName || ''
                }
              });
            }
            isValid = false;
          }
          
          if (item.barcode && existingBarcodes.has(item.barcode.toLowerCase())) {
            const existing = existingItems.find(e => e.barcode?.toLowerCase() === item.barcode.toLowerCase());
            // Aynı ürün için birden fazla çakışma eklenmesini önle
            const alreadyAdded = dbConflicts.some(c => 
              c.excelItem.row === item.row
            );
            if (!alreadyAdded) {
              dbConflicts.push({
                name: item.name,
                stockCode: item.stockCode,
                barcode: item.barcode,
                reason: `Barkod veritabanında "${existing?.name || 'başka ürün'}" ile çakışıyor`,
                excelItem: {
                  row: item.row,
                  name: item.name || '',
                  stockCode: item.stockCode || '',
                  barcode: item.barcode || '',
                  salePrice: item.salePrice || 0,
                  purchasePrice: item.purchasePrice || 0,
                  quantity: item.quantity || 0,
                  unit: item.unit || '',
                  categoryName: item.categoryPath || '',
                  brandName: item.brandId?.toString() || ''
                },
                existingItem: {
                  id: existing?.id || '',
                  name: existing?.name || '',
                  stockCode: existing?.stockCode || '',
                  barcode: existing?.barcode || '',
                  salePrice: existing?.salePrice || 0,
                  purchasePrice: existing?.purchasePrice || 0,
                  quantity: existing?.warehouseStocks?.reduce((sum: number, ws: any) => sum + ws.quantity, 0) || 0,
                  unit: existing?.unit || '',
                  categoryName: existing?.categoryName || '',
                  brandName: existing?.brandName || ''
                }
              });
            }
            isValid = false;
          }
          
          // 3. Excel içi duplikasyon kontrolü
          // Mevcut item bilgilerini hazırla (Map'e eklemek ve duplikasyon kaydı için)
          const currentItemInfo = {
            row: item.row,
            name: item.name || '',
            stockCode: item.stockCode || '',
            barcode: item.barcode || '',
            salePrice: item.salePrice || 0,
            purchasePrice: item.purchasePrice || 0,
            quantity: item.quantity || 0,
            unit: item.unit || '',
            categoryName: item.categoryPath || '',
            brandName: item.brandId || ''
          };
          
          if (item.stockCode) {
            const existingInExcel = excelStockCodes.get(item.stockCode.toLowerCase());
            if (existingInExcel) {
              excelDuplicates.push({
                name: item.name,
                stockCode: item.stockCode,
                barcode: item.barcode,
                duplicateOf: existingInExcel.name,
                row: item.row,
                // ✅ Orijinal ürün bilgileri (ilk kaydedilen)
                originalItem: {
                  row: existingInExcel.row,
                  name: existingInExcel.name,
                  stockCode: existingInExcel.stockCode,
                  barcode: existingInExcel.barcode,
                  salePrice: existingInExcel.salePrice,
                  purchasePrice: existingInExcel.purchasePrice,
                  quantity: existingInExcel.quantity,
                  unit: existingInExcel.unit,
                  categoryName: existingInExcel.categoryName,
                  brandName: existingInExcel.brandName
                },
                // ✅ Duplike ürün bilgileri (şu anki)
                duplicateItem: currentItemInfo
              });
              isValid = false;
            } else {
              excelStockCodes.set(item.stockCode.toLowerCase(), currentItemInfo);
            }
          }
          
          if (item.barcode && item.barcode.trim() !== '') {
            const existingInExcel = excelBarcodes.get(item.barcode.toLowerCase());
            if (existingInExcel) {
              // Eğer zaten stok kodu duplikasyonu olarak eklenmemişse ekle
              const alreadyAdded = excelDuplicates.some(d => 
                d.stockCode === item.stockCode && d.barcode === item.barcode && d.row === item.row
              );
              if (!alreadyAdded) {
                excelDuplicates.push({
                  name: item.name,
                  stockCode: item.stockCode,
                  barcode: item.barcode,
                  duplicateOf: existingInExcel.name,
                  row: item.row,
                  // ✅ Orijinal ürün bilgileri (ilk kaydedilen)
                  originalItem: {
                    row: existingInExcel.row,
                    name: existingInExcel.name,
                    stockCode: existingInExcel.stockCode,
                    barcode: existingInExcel.barcode,
                    salePrice: existingInExcel.salePrice,
                    purchasePrice: existingInExcel.purchasePrice,
                    quantity: existingInExcel.quantity,
                    unit: existingInExcel.unit,
                    categoryName: existingInExcel.categoryName,
                    brandName: existingInExcel.brandName
                  },
                  // ✅ Duplike ürün bilgileri (şu anki)
                  duplicateItem: currentItemInfo
                });
                isValid = false;
              }
            } else {
              excelBarcodes.set(item.barcode.toLowerCase(), currentItemInfo);
            }
          }
          
          if (isValid) validCount++;
        }
        
        // Sonuçları kaydet
        const invalidCount = dbConflicts.length + excelDuplicates.length + emptyRequired.length;
        
        setValidationResult({
          loading: false,
          totalItems: allItems.length,
          validItems: validCount,
          invalidItems: allItems.length - validCount,
          dbConflicts,
          excelDuplicates,
          emptyRequired
        });
        
        console.log(`✅ [VALIDATION] Doğrulama tamamlandı: ${validCount} geçerli, ${allItems.length - validCount} hatalı`);
        
        setStep(2); // Önizleme adımına geç
      } catch (error) {
        console.error('Excel dosyası okunurken hata:', error);
        setError('Excel dosyası okunurken bir hata oluştu.');
        setValidationResult(null);
      } finally {
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Dosya okuma hatası.');
      setLoading(false);
      setValidationResult(null);
    };
    
    reader.readAsArrayBuffer(file);
  };
  
  // Boş barkod ayarları - Varsayılan: Otomatik barkod oluştur
  const [skipEmptyBarcodes, setSkipEmptyBarcodes] = useState<boolean>(false);
  const [autoGeneratedBarcodes, setAutoGeneratedBarcodes] = useState<number>(0);
  
  // Yükleme ilerlemesi
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  
  // Otomatik barkod oluştur
  const generateBarcode = (): string => {
    // "GTS" ile başlayan, 10 haneli rastgele sayı
    const randomNum = Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
    return `GTS${randomNum}`;
  };

  // Stok kodu boş bırakılan ürünler için, halihazırda kullanılan kodlarla
  // çakışmayan bir kod üretir. Ad önekini korur, çakışırsa rakam kısmını
  // büyütür; son çare olarak sayaç ekler (üretim asla başarısız olmaz).
  const generateUniqueStockCode = (name: string | undefined, usedCodes: Set<string>): string => {
    const prefix = (name || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .substring(0, 2) || 'ST';

    for (let attempt = 0; attempt < 50; attempt++) {
      const digits = Math.floor(1000 + Math.random() * 9000);
      const candidate = `${prefix}${digits}`;
      if (!usedCodes.has(candidate.toLowerCase())) return candidate;
    }
    for (let attempt = 0; attempt < 200; attempt++) {
      const digits = Math.floor(100000 + Math.random() * 900000);
      const candidate = `${prefix}${digits}`;
      if (!usedCodes.has(candidate.toLowerCase())) return candidate;
    }
    let counter = 1;
    let candidate = `${prefix}-${Date.now().toString(36).toUpperCase()}`;
    while (usedCodes.has(candidate.toLowerCase())) {
      candidate = `${prefix}-${Date.now().toString(36).toUpperCase()}-${counter++}`;
    }
    return candidate;
  };
  
  // NOT: Excel'deki resim URL'leri artık indirilmez/Image Service'e kopyalanmaz;
  // harici URL doğrudan ürünün ImageUrl alanına yazılır (bkz. ADIM 2.7).
  
  // Verileri Firebase'e kaydet
  const handleSaveData = async () => {
    console.log('🚀 [IMPORT] handleSaveData başladı');
    console.log('🚀 [IMPORT] file:', file);
    console.log('🚀 [IMPORT] selectedSheet:', selectedSheet);
    console.log('🚀 [IMPORT] companyId:', companyId);
    
    if (!file || !selectedSheet || !companyId) {
      console.error('❌ [IMPORT] Gerekli bilgiler eksik');
      setError('Gerekli bilgiler eksik.');
      return;
    }
    
    // ✅ Merkez Depo kontrolü
    const centralWarehouse = warehouses.find(w => 
      w.name.toLowerCase().includes('merkez') ||
      w.name.toLowerCase() === 'merkez depo' ||
      w.name.toLowerCase() === 'ana depo'
    );
    
    if (!centralWarehouse) {
      console.error('❌ [IMPORT] Merkez Depo bulunamadı');
      setError('Merkez Depo bulunamadı! Lütfen önce "Merkez Depo" adında bir depo oluşturun veya Depo sayfasına bir kere girin.');
      return;
    }
    
    console.log('✅ [IMPORT] Merkez Depo bulundu:', centralWarehouse.name);
    console.log('✅ [IMPORT] Tüm gerekli bilgiler mevcut, işlem başlıyor...');
    
    // İptal flag'ini reset et
    setIsCancelled(false);
    
    setSavingData(true);
    setError(null);
    // Çakışmaları temizle
    setBarcodeConflicts([]);
    // İlerlemeyi sıfırla
    setLoadingProgress(0);
    setAutoGeneratedBarcodes(0);
    
    try {
      console.log('📖 [IMPORT] Excel dosyası okunuyor...');
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          console.log('✅ [IMPORT] Excel dosyası okundu, işleniyor...');
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Seçilen sheet'i al
          const worksheet = workbook.Sheets[selectedSheet];
          
          // JSON'a çevir
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 'A', range: startRow - 1 });
          
          console.log('📊 [IMPORT] Excel verileri:', jsonData.length, 'satır');
          
          // Ürün listesi hazırla
          const inventoryItems: Record<string, any>[] = [];
          
          // Yeni kategoriler ve markalar için listeler
          // Kategori zincirleri: anahtar = küçük harfli yol, değer = orijinal isimli zincir dizisi
          const uniqueCategoryChains = new Map<string, string[]>();
          const uniqueBrands = new Set<string>();
          const uniqueWarehouses = new Set<string>();
          
          // Otomatik oluşturulacak barkod sayısını sıfırla
          let autoGenBarcodeCount = 0;
          
          // Tüm verileri işle, kategorileri ve markaları topla
          for (const row of jsonData as Record<string, any>[]) {
            // Eğer boş satır filtresi aktifse ve satırdaki dolu sütun sayısı yeterli değilse, bu satırı atla
            if (enableEmptyRowFilter && countFilledColumns(row) < minRequiredColumns) {
              continue;
            }
            
            // Yeni ürün objesi oluştur
            const newItem: Record<string, any> = {
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            
            // Geçici değişkenler - ÖTV oranı için
            let sctRateValue = 0;
            let brandName = '';
            let warehouseName = '';
            let hasBarcode = false;
            
            // Eşleştirmelere göre verileri ekle
            columnMappings.forEach(mapping => {
              // Excel sütunu seçimi yapıldıysa değeri al
              if (mapping.showColumnSelect && mapping.selected && row[mapping.selected] !== undefined) {
                // Barkod işleme
                if (mapping.fieldName === 'barcode') {
                  const barcodeValue = row[mapping.selected];
                  if (barcodeValue && barcodeValue.toString().trim() !== '') {
                    hasBarcode = true;
                    newItem[mapping.fieldName] = barcodeValue.toString().trim();
                  }
                }
                // Sayısal değerleri sayıya çevir
                else if (['quantity', 'purchasePrice', 'salePrice', 'vatRate', 'sctRate'].includes(mapping.fieldName)) {
                  const value = row[mapping.selected];
                  const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                  newItem[mapping.fieldName] = numValue;
                  
                  // ÖTV oranını sakla
                  if (mapping.fieldName === 'sctRate') {
                    sctRateValue = numValue;
                  }
                } 
                // Kategori seviyeleri: isimler geçici saklanır, satır sonunda zincire çevrilir
                else if (['categoryMain', 'categorySub1', 'categorySub2'].includes(mapping.fieldName)) {
                  newItem[mapping.fieldName] = row[mapping.selected]?.toString().trim() || '';
                }
                else if (mapping.fieldName === 'brandId') {
                  // Marka adını şimdilik brandId olarak kaydedelim
                  // Daha sonra gerçek ID ile değiştirilecek
                  brandName = row[mapping.selected]?.toString().trim() || '';
                  newItem[mapping.fieldName] = brandName; // İsmi geçici olarak kaydediyoruz
                  
                  // Eğer marka adı varsa ve mevcut markalarda yoksa, yeni marka olarak işaretle
                  if (brandName && !existingBrands.some(brand => brand.name.toLowerCase() === brandName.toLowerCase())) {
                    uniqueBrands.add(brandName);
                  }
                }
                else if (mapping.fieldName === 'warehouseId') {
                  // ✅ Artık Excel'den depo seçilmiyor, otomatik olarak Merkez Depo atanacak
                  // Bu kod bloğu artık çalışmayacak çünkü showColumnSelect: false
                  // Merkez Depo ID'si customValue'dan gelecek
                }
                else {
                  newItem[mapping.fieldName] = row[mapping.selected];
                }
              } 
              // Özel değer varsa onu kullan
              else if (!mapping.showColumnSelect && mapping.customValue !== null) {
                newItem[mapping.fieldName] = mapping.customValue;
              }
            });
            
            // Resim URL: metin URL değilse hücrenin köprü (hyperlink) hedefinden oku
            const imgMappingImport = columnMappings.find(m => m.fieldName === 'imageUrl');
            if (imgMappingImport?.showColumnSelect && imgMappingImport.selected) {
              const resolvedImageUrl = resolveImageCellValue(worksheet, imgMappingImport.selected, row);
              newItem.imageUrl = resolvedImageUrl || undefined;
            }
            
            // Kategori zinciri (ana → alt → alt2): ürüne geçici yazılır; isim→id
            // çözümlemesi kategoriler oluşturulduktan SONRA yapılır (üst kategoriye göre)
            const categoryChain = buildCategoryChain(
              newItem.categoryMain,
              newItem.categorySub1,
              newItem.categorySub2
            );
            newItem.categoryChain = categoryChain;
            if (categoryChain.length > 0) {
              uniqueCategoryChains.set(categoryChain.map(n => n.toLowerCase()).join('\u001f'), categoryChain);
            }
            
            // Barkod kontrolü - eğer yoksa otomatik oluştur veya eklemeden geç
            if (!hasBarcode) {
              // Boş barkodlu ürünleri atla seçeneği açıksa, bu ürünü atlayalım
              if (skipEmptyBarcodes) {
                console.log('⏭️ [IMPORT] Barkod yok, ürün atlanıyor:', newItem.name);
                continue;
              } else {
                // Otomatik barkod oluştur
                newItem.barcode = generateBarcode();
                autoGenBarcodeCount++;
                console.log('🔢 [IMPORT] Otomatik barkod oluşturuldu:', newItem.barcode, 'için', newItem.name);
              }
            }
            
            // YENİ: Stok eksiye düşebilir ayarını ekle
            newItem.canBeNegative = canBeNegative;
            
            console.log('✅ [IMPORT] Ürün işlendi:', newItem.name || newItem.stockCode || 'İsimsiz', {
              name: newItem.name,
              salePrice: newItem.salePrice,
              quantity: newItem.quantity,
              barcode: newItem.barcode,
              unit: newItem.unit
            });
            
            // Ürünü listeye ekle
            inventoryItems.push(newItem);
          }
          
          // Otomatik oluşturulan barkod sayısını güncelle
          setAutoGeneratedBarcodes(autoGenBarcodeCount);
          
          // ✅ YENİ: Toplam resim sayısını hesapla
          let totalImageCount = 0;
          inventoryItems.forEach(item => {
            if (item.imageUrl && item.imageUrl.trim() !== '') {
              const urls = item.imageUrl.split(',').map((u: string) => u.trim()).filter((u: string) => u !== '');
              totalImageCount += urls.length;
            }
          });
          
          console.log('📊 [IMPORT] Toplam Resim:', totalImageCount);
          
          // Oluşturulacak YENİ kategori düğümü sayısı (simülasyon): zincirler mevcut
          // ağaca üst kategoriye göre eşlenir, bulunamayan her seviye yeni düğümdür.
          const simulatedCategories: Array<{ id: string; name: string; parentId: string | null }> =
            existingCategories.map(c => ({ id: c.id, name: c.name, parentId: c.parentId ?? null }));
          let newCategoryCount = 0;
          uniqueCategoryChains.forEach(chain => {
            let parentId: string | null = null;
            for (const name of chain) {
              const found = findCategoryInList(simulatedCategories, name, parentId);
              if (found) {
                parentId = found.id;
                continue;
              }
              newCategoryCount++;
              const tmpId = `__new_${newCategoryCount}`;
              simulatedCategories.push({ id: tmpId, name, parentId });
              parentId = tmpId;
            }
          });
          
          // Kaydetme durumunu sıfırla ve toplam sayıları ayarla
          setSavingStatus({
            categories: { total: newCategoryCount, saved: 0, loading: false },
            brands: { total: uniqueBrands.size, saved: 0, loading: false },
            images: { total: totalImageCount, saved: 0, failed: 0, loading: false }, // YENİ
            inventory: { total: inventoryItems.length, saved: 0, failed: 0, loading: false }
          });
          
          // Adım 3'e geç
          setStep(3);
          
          console.log('📝 [IMPORT] Kaydetme başlıyor...');
          console.log('📝 [IMPORT] Kategori zinciri:', uniqueCategoryChains.size, '| Yeni kategori düğümü:', newCategoryCount);
          console.log('📝 [IMPORT] Toplam Marka:', uniqueBrands.size);
          console.log('📝 [IMPORT] Toplam Depo:', uniqueWarehouses.size);
          console.log('📝 [IMPORT] Toplam Resim:', totalImageCount); // YENİ
          console.log('📝 [IMPORT] Toplam Ürün:', inventoryItems.length);

          // Yeni oluşturulan kategori/markalar state'e (existingCategories/existingBrands)
          // bu fonksiyon kapanışında yansımaz (stale closure). Bu yüzden taze listeleri
          // yerel değişkende tutup isim→id çözümlemesinde bunları kullanıyoruz.
          let latestCategories = existingCategories;
          let latestBrands = existingBrands;
          
          // ADIM 1: Önce kategorileri kaydet (SQL API) — zincirler hiyerarşiyle oluşturulur
          if (newCategoryCount > 0 && !isCancelled) {
            console.log('🏷️ [IMPORT] Kategoriler kaydediliyor (hiyerarşik)...');
            // Kategori kaydetme durumunu güncelle
            setSavingStatus(prev => ({
              ...prev,
              categories: { ...prev.categories, loading: true }
            }));
            
            setLoadingProgress(5); // İlerleme göstergesi
            
            try {
              let savedCount = 0;
              
              // Çalışma listesi: mevcut + bu döngüde oluşturulanlar (üst kategoriye göre eşleştirme)
              const workingCategories: Array<{ id: string; name: string; parentId: string | null }> =
                latestCategories.map(c => ({ id: c.id, name: c.name, parentId: c.parentId ?? null }));
              
              // Her zinciri kökten yaprağa yürü; eksik seviyeyi üstüne bağlayarak oluştur
              for (const chain of Array.from(uniqueCategoryChains.values())) {
                // İşlem iptal edildiyse döngüden çık
                if (isCancelled) break;
                
                let parentId: string | null = null;
                for (const name of chain) {
                  if (isCancelled) break;
                  
                  const found = findCategoryInList(workingCategories, name, parentId);
                  if (found) {
                    parentId = found.id;
                    continue;
                  }
                  
                  console.log('🏷️ [IMPORT] Kategori kaydediliyor:', name, '| üst:', parentId ?? '(ana kategori)');
                  
                  // SQL API ile yeni kategori ekle (parentId ile hiyerarşi kurulur)
                  try {
                    const created = await CategoryService.createCategory({
                      companyId,
                      name: name.trim(),
                      parentId,
                    });
                    workingCategories.push({ id: created.id, name: created.name, parentId: created.parentId ?? null });
                    parentId = created.id;
                    console.log('✅ [IMPORT] Kategori kaydedildi:', name);
                  } catch (error) {
                    console.error('❌ [IMPORT] Kategori kaydetme hatası:', name, error);
                    throw error;
                  }
                  
                  // Kaydedilen kategori sayısını güncelle
                  savedCount++;
                  setSavingStatus(prev => ({
                    ...prev,
                    categories: { ...prev.categories, saved: savedCount }
                  }));
                  
                  // İlerleme göstergesini güncelle - kategoriler için %5-%15 arası
                  const progress = 5 + (savedCount / Math.max(1, newCategoryCount)) * 10;
                  setLoadingProgress(Math.min(15, progress));
                }
              }
              
              // Kategori kaydetme işlemi tamamlandı
              setSavingStatus(prev => ({
                ...prev,
                categories: { ...prev.categories, loading: false }
              }));
              
              // İşlem iptal edilmediyse kategorileri yeniden yükle (taze liste yerel değişkene)
              if (!isCancelled) {
                latestCategories = await loadCategories(companyId);
              }
              
            } catch (error) {
              console.error('Kategoriler kaydedilirken hata:', error);
              setSavingStatus(prev => ({
                ...prev,
                categories: { ...prev.categories, loading: false }
              }));
            }
          } else {
            setLoadingProgress(15); // Kategori yoksa direkt %15'e atla
          }
          
          // ADIM 2: Sonra markaları kaydet (SQL API)
          if (uniqueBrands.size > 0 && !isCancelled) {
            console.log('🏭 [IMPORT] Markalar kaydediliyor...');
            // Marka kaydetme durumunu güncelle
            setSavingStatus(prev => ({
              ...prev,
              brands: { ...prev.brands, loading: true }
            }));
            
            try {
              let savedCount = 0;
              
              // Yeni markaları tek tek kaydet
              for (const brandName of Array.from(uniqueBrands)) {
                // İşlem iptal edildiyse döngüden çık
                if (isCancelled) break;
                
                // SQL API ile yeni marka ekle
                await BrandService.createBrand({
                  companyId,
                  name: brandName.trim(),
                  isActive: true,
                });
                
                // Kaydedilen marka sayısını güncelle
                savedCount++;
                setSavingStatus(prev => ({
                  ...prev,
                  brands: { ...prev.brands, saved: savedCount }
                }));
                
                // İlerleme göstergesini güncelle - markalar için %15-%25 arası
                const progress = 15 + (savedCount / uniqueBrands.size) * 10;
                setLoadingProgress(Math.min(25, progress));
              }
              
              // Marka kaydetme işlemi tamamlandı
              setSavingStatus(prev => ({
                ...prev,
                brands: { ...prev.brands, loading: false }
              }));
              
              // İşlem iptal edilmediyse markaları yeniden yükle (taze liste yerel değişkene)
              if (!isCancelled) {
                latestBrands = await loadBrands(companyId);
              }
              
            } catch (error) {
              console.error('Markalar kaydedilirken hata:', error);
              setSavingStatus(prev => ({
                ...prev,
                brands: { ...prev.brands, loading: false }
              }));
            }
          } else {
            setLoadingProgress(25); // Marka yoksa direkt %25'e atla
          }
          
          // ADIM 2.5: Depoları kaydet (SQL API)
          if (uniqueWarehouses.size > 0 && !isCancelled) {
            console.log('🏭 [IMPORT] Depolar kaydediliyor...');
            
            try {
              let savedCount = 0;
              
              // Yeni depoları tek tek kaydet
              for (const warehouseName of Array.from(uniqueWarehouses)) {
                // İşlem iptal edildiyse döngüden çık
                if (isCancelled) break;
                
                console.log('🏭 [IMPORT] Depo kaydediliyor:', warehouseName);
                
                // SQL API ile yeni depo ekle
                try {
                  await WarehouseService.createWarehouse({
                    companyId,
                    name: warehouseName.trim(),
                    isDefault: false, // Yeni depolar varsayılan değil
                  });
                  console.log('✅ [IMPORT] Depo kaydedildi:', warehouseName);
                } catch (error) {
                  console.error('❌ [IMPORT] Depo kaydetme hatası:', warehouseName, error);
                  // Depo kayıt hatası olsa da devam edelim
                }
                
                savedCount++;
                
                // İlerleme göstergesini güncelle - depolar için %25-%30 arası
                const progress = 25 + (savedCount / uniqueWarehouses.size) * 5;
                setLoadingProgress(Math.min(30, progress));
              }
              
              // ✅ Depolar artık AuthContext'ten otomatik güncelleniyor
              
            } catch (error) {
              console.error('Depolar kaydedilirken hata:', error);
            }
          } else {
            setLoadingProgress(30); // Depo yoksa direkt %30'a atla
          }
          
          // ✅ ADIM 2.7: Resim URL'leri ürünlere DOĞRUDAN atanır (indirme/kopyalama YOK).
          // Resimler Image Service'e kopyalanmaz; tedarikçinin URL'si InventoryItems.ImageUrl
          // alanına aynen yazılır. Not: kaynak site resmi kaldırırsa üründe resim görünmez
          // olur — bu harici bağımlılık bilinçli olarak kabul edildi (müşteri talebi).
          if (totalImageCount > 0 && !isCancelled) {
            console.log('🖼️ [IMPORT] Resim URL\'leri ürünlere doğrudan atanıyor (indirme yok)...');
            
            setSavingStatus(prev => ({
              ...prev,
              images: { ...prev.images, loading: true }
            }));
            
            let assignedCount = 0;
            for (const item of inventoryItems) {
              if (item.imageUrl && String(item.imageUrl).trim() !== '') {
                const urls = String(item.imageUrl).split(',').map((u: string) => u.trim()).filter((u: string) => u !== '');
                if (urls.length > 0) {
                  // İlk URL ana resim; birden fazlaysa tamamı imageUrls'e
                  item.imageUrl = urls[0];
                  if (urls.length > 1) item.imageUrls = urls;
                  assignedCount += urls.length;
                } else {
                  item.imageUrl = undefined;
                }
              }
            }
            
            console.log(`✅ [IMPORT] ${assignedCount} resim URL'si ürünlere doğrudan atandı`);
            
            setSavingStatus(prev => ({
              ...prev,
              images: { ...prev.images, loading: false, saved: assignedCount, failed: 0 }
            }));
            setLoadingProgress(50);
          } else {
            setLoadingProgress(50); // Resim yoksa direkt %50'ye atla
          }
          
          // ADIM 3: Son olarak stok ürünlerini kaydet (SQL API)
          if (inventoryItems.length > 0 && !isCancelled) {
            console.log('📦 [IMPORT] Ürünler kaydediliyor...');
            // Stok kaydetme durumunu güncelle
            setSavingStatus(prev => ({
              ...prev,
              inventory: { ...prev.inventory, loading: true }
            }));
            
            try {
              // Önce tüm mevcut inventory itemları yükleyelim (stok kodu ve barkod kontrolü için)
              console.log('📋 [IMPORT] Mevcut ürünler yükleniyor...');
              const existingItemsList = await InventoryService.getAllItemsSQL(companyId);
              console.log('✅ [IMPORT] Mevcut ürün sayısı:', existingItemsList.length);
              
              // ✅ İlk 5 ürünün stok kodlarını logla (debug için)
              if (existingItemsList.length > 0) {
                console.log('📋 [IMPORT] İlk 5 mevcut ürün stok kodları:', 
                  existingItemsList.slice(0, 5).map(item => ({ name: item.name, stockCode: item.stockCode }))
                );
              }
              
              setLoadingProgress(30); // Veri yükleme tamamlandı
              
              const conflicts: BarcodeConflict[] = [];
              const failedProductsList: Array<{ name: string, stockCode: string, barcode: string, error: string }> = [];
              
              let savedCount = 0;
              let failedCount = 0;
              
              // ✅ YENİ BULK API SİSTEMİ: Ürünleri hazırla ve topla
              const itemsToCreate: BulkCreateItemDto[] = [];
              const existingStockCodes = new Set(existingItemsList.map(e => e.stockCode?.toLowerCase()).filter(Boolean));
              const existingBarcodes = new Set(existingItemsList.map(e => e.barcode?.toLowerCase()).filter(Boolean));
              
              // ✅ Excel içi duplikasyon kontrolü için - hangi ürünlerin hangi stok kodlarına sahip olduğunu takip et
              const excelItemsByStockCode = new Map<string, { name: string, index: number }>();
              const excelItemsByBarcode = new Map<string, { name: string, index: number }>();
              
              console.log('📦 [IMPORT] Ürünler hazırlanıyor (Bulk API için)...');
              
              // Kategori arama listesi (taze liste — yeni oluşturulan hiyerarşi dahil)
              const categoryLookup = latestCategories.map(c => ({
                id: c.id,
                name: c.name,
                parentId: c.parentId ?? null,
              }));
              
              // Her bir ürünü hazırla
              for (let i = 0; i < inventoryItems.length; i++) {
                // İşlem iptal edildiyse döngüden çık
                if (isCancelled) break;
                
                const item = inventoryItems[i];
                
                // ✅ Stok kodu ve barkod kontrolü (frontend'de)
                let hasConflict = false;
                let conflictReason = '';
                let isExcelDuplicate = false; // Excel içi duplikasyon mı?
                
                // Stok kodu kontrolü - önce veritabanında, sonra Excel içinde
                if (item.stockCode && existingStockCodes.has(item.stockCode.toLowerCase())) {
                  hasConflict = true;
                  const existingItem = existingItemsList.find(e => e.stockCode?.toLowerCase() === item.stockCode.toLowerCase());
                  if (existingItem) {
                    conflictReason = `Stok kodu çakışması: Bu stok kodu veritabanında "${existingItem.name}" ürününde kullanılıyor`;
                  } else {
                    // Excel içi duplikasyon
                    isExcelDuplicate = true;
                    const excelItem = excelItemsByStockCode.get(item.stockCode.toLowerCase());
                    conflictReason = `Excel içi duplikasyon: Bu stok kodu Excel'de "${excelItem?.name || 'başka bir ürün'}" (satır ${(excelItem?.index || 0) + 1}) ile aynı`;
                  }
                  console.warn(`⚠️ [IMPORT] Stok kodu çakışması: ${item.stockCode}`);
                }
                
                // Barkod kontrolü - önce veritabanında, sonra Excel içinde
                if (!hasConflict && item.barcode && item.barcode.trim() !== '' && existingBarcodes.has(item.barcode.toLowerCase())) {
                  hasConflict = true;
                  const existingItem = existingItemsList.find(e => e.barcode?.toLowerCase() === item.barcode.toLowerCase());
                  if (existingItem) {
                    conflictReason = `Barkod çakışması: Bu barkod veritabanında "${existingItem.name}" ürününde kullanılıyor`;
                  } else {
                    // Excel içi duplikasyon
                    isExcelDuplicate = true;
                    const excelItem = excelItemsByBarcode.get(item.barcode.toLowerCase());
                    conflictReason = `Excel içi duplikasyon: Bu barkod Excel'de "${excelItem?.name || 'başka bir ürün'}" (satır ${(excelItem?.index || 0) + 1}) ile aynı`;
                  }
                  console.warn(`⚠️ [IMPORT] Barkod çakışması: ${item.barcode}`);
                }
                
                // ✅ Çakışma varsa atla (hem veritabanı hem Excel içi duplikasyonlar)
                if (hasConflict) {
                  const conflictingItem = existingItemsList.find(e => 
                    e.stockCode?.toLowerCase() === item.stockCode?.toLowerCase() || 
                    e.barcode?.toLowerCase() === item.barcode?.toLowerCase()
                  );
                  
                  // Çakışma listesine ekle (veritabanı çakışması için detaylı bilgi, Excel duplikasyonu için temel bilgi)
                  conflicts.push({
                    barcode: item.barcode || item.stockCode,
                    importItem: { ...item },
                    existingItem: conflictingItem ? {
                      id: conflictingItem.id,
                      name: conflictingItem.name || '',
                      stockCode: conflictingItem.stockCode || '',
                      categoryName: existingCategories.find(c => c.id === conflictingItem.categoryId)?.name || '',
                      warehouseName: '',
                      brandName: existingBrands.find(b => b.id === conflictingItem.brandId)?.name || '',
                      quantity: conflictingItem.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
                      unit: conflictingItem.unit
                    } : {
                      // Excel içi duplikasyon - minimal bilgi
                      id: '',
                      name: isExcelDuplicate ? 'Excel içi duplikasyon' : 'Bilinmeyen',
                      stockCode: item.stockCode || '',
                      categoryName: '',
                      warehouseName: '',
                      brandName: '',
                      quantity: 0,
                      unit: ''
                    }
                  });
                  
                  // Başarısız ürün listesine her zaman ekle
                  failedProductsList.push({
                    name: item.name,
                    stockCode: item.stockCode || '',
                    barcode: item.barcode || '',
                    error: conflictReason
                  });
                  failedCount++;
                  
                  continue;
                }
                
                // Kategori referansı: zincir kökten yaprağa yürünür (üst kategoriye göre
                // eşleştirme), ürün en derin bulunan seviyenin id'sine bağlanır
                const itemChain: string[] = Array.isArray(item.categoryChain) ? item.categoryChain : [];
                if (itemChain.length > 0) {
                  let chainParentId: string | null = null;
                  let resolvedCategoryId: string | undefined;
                  for (const levelName of itemChain) {
                    const cat = findCategoryInList(categoryLookup, levelName, chainParentId);
                    if (!cat) break;
                    resolvedCategoryId = cat.id;
                    chainParentId = cat.id;
                  }
                  item.categoryId = resolvedCategoryId;
                } else {
                  item.categoryId = undefined;
                }
                
                if (item.brandId) {
                  const brandName = item.brandId.toString().trim();
                  const brand = latestBrands.find(b => 
                    b.name.toLowerCase() === brandName.toLowerCase()
                  );
                  item.brandId = brand?.id || undefined;
                }
                
                // Varsayılan değerler
                if (!item.stockCode) {
                  item.stockCode = generateUniqueStockCode(item.name, existingStockCodes);
                }
                if (!item.unit || item.unit.toString().trim() === '') item.unit = 'adet';
                if (!item.purchaseCurrency) item.purchaseCurrency = currencies.length > 0 ? currencies[0].code : 'TRY';
                if (!item.saleCurrency) item.saleCurrency = currencies.length > 0 ? currencies[0].code : 'TRY';
                
                // KDV Durumu normalize
                if (item.vatStatus) {
                  const vatStatusStr = item.vatStatus.toString().toLowerCase().trim();
                  if (['dahil', 'included', '1', 'true'].includes(vatStatusStr)) {
                    item.vatStatus = 'included';
                  } else if (['hariç', 'haric', 'excluded', '0', 'false'].includes(vatStatusStr)) {
                    item.vatStatus = 'excluded';
                  } else {
                    item.vatStatus = 'included';
                  }
                } else {
                  item.vatStatus = 'included';
                }
                if (!item.vatRate) item.vatRate = 20;
                
                // E-Ticaret Durumu normalize
                if (item.isEcommerce !== undefined && item.isEcommerce !== null) {
                  const ecommerceStr = item.isEcommerce.toString().toLowerCase().trim();
                  item.isEcommerce = ['evet', 'yes', 'true', '1'].includes(ecommerceStr);
                } else {
                  item.isEcommerce = false;
                }
                
                // ÖTV durumu
                if ((item.sctRate || 0) > 0) {
                  item.sctStatus = 'excluded';
                } else {
                  item.sctStatus = undefined;
                  item.sctRate = undefined;
                }
                
                // ✅ Bulk API için DTO oluştur
                const bulkItemDto: BulkCreateItemDto = {
                  stockCode: item.stockCode,
                  name: item.name,
                  categoryId: item.categoryId || undefined,
                  brandId: item.brandId || undefined,
                  quantity: item.quantity || 0, // ✅ Miktar dahil
                  purchasePrice: item.purchasePrice || undefined,
                  purchaseCurrency: item.purchaseCurrency || 'TRY',
                  salePrice: item.salePrice,
                  saleCurrency: item.saleCurrency || 'TRY',
                  listPrice: item.listPrice || undefined,
                  unit: item.unit || 'adet',
                  canBeNegative: item.canBeNegative !== undefined ? item.canBeNegative : true,
                  description: item.description || undefined,
                  barcode: item.barcode || undefined,
                  vatRate: item.vatRate || 20,
                  vatStatus: item.vatStatus || 'included',
                  sctRate: item.sctRate || undefined,
                  sctStatus: item.sctStatus || undefined,
                  imageUrl: item.imageUrl || undefined,
                  imageUrls: item.imageUrls || undefined,
                  branchesId: item.branchesId || undefined,
                  departmentNo: item.departmentNo ? Number(item.departmentNo) : undefined,
                  isEcommerce: item.isEcommerce || false,
                  isActive: true,
                };
                
                itemsToCreate.push(bulkItemDto);
                
                // Eklenen stok kodu ve barkodu set'e ekle (duplikasyonu önlemek için)
                if (item.stockCode) {
                  existingStockCodes.add(item.stockCode.toLowerCase());
                  excelItemsByStockCode.set(item.stockCode.toLowerCase(), { name: item.name, index: i });
                }
                if (item.barcode) {
                  existingBarcodes.add(item.barcode.toLowerCase());
                  excelItemsByBarcode.set(item.barcode.toLowerCase(), { name: item.name, index: i });
                }
                
                // İlerleme göstergesini güncelle - hazırlık için %30-%50 arası
                const prepProgress = 30 + ((i + 1) / inventoryItems.length) * 20;
                setLoadingProgress(Math.min(50, prepProgress));
              }
              
              console.log(`📦 [IMPORT] ${itemsToCreate.length} ürün hazırlandı, ${failedCount} çakışma atlandı`);

              // 🏷️ [KATEGORİ TEŞHİS] Kayıt anında kategori id'si atanmış mı?
              {
                const withCat = itemsToCreate.filter(it => !!it.categoryId).length;
                console.log(`🏷️ [KATEGORİ TEŞHİS] Kategori ID atanan ürün: ${withCat}/${itemsToCreate.length} (kategorisiz: ${itemsToCreate.length - withCat})`);
                console.log(`🏷️ [KATEGORİ TEŞHİS] Çözümlemede kullanılan kategori sayısı (latestCategories): ${latestCategories.length}`);
                console.log('🏷️ [KATEGORİ TEŞHİS] Örnek DTO (ilk 3):',
                  itemsToCreate.slice(0, 3).map(it => ({ ürün: it.name, categoryId: it.categoryId ?? '(YOK)', brandId: it.brandId ?? '(YOK)' })));
              }
              
              // ✅ BULK API ile ürünleri 100'er batch'ler halinde kaydet
              if (itemsToCreate.length > 0 && !isCancelled) {
                const BATCH_SIZE = 100;
                const totalBatches = Math.ceil(itemsToCreate.length / BATCH_SIZE);
                
                console.log(`🚀 [IMPORT] Bulk API ile ${totalBatches} batch gönderiliyor (her batch ${BATCH_SIZE} ürün)...`);
                
                for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
                  if (isCancelled) break;
                  
                  const start = batchIndex * BATCH_SIZE;
                  const end = Math.min(start + BATCH_SIZE, itemsToCreate.length);
                  const batch = itemsToCreate.slice(start, end);
                  
                  console.log(`📦 [IMPORT] Batch ${batchIndex + 1}/${totalBatches} gönderiliyor (${batch.length} ürün)...`);
                  
                  try {
                    const result = await InventoryService.bulkCreateItems(
                      companyId,
                      centralWarehouse.id,
                      batch
                    );
                    
                    savedCount += result.successCount;
                    failedCount += result.failedCount;
                    
                    // Backend'den dönen hataları ekle
                    if (result.failedItems && result.failedItems.length > 0) {
                      result.failedItems.forEach(failedItem => {
                        failedProductsList.push({
                          name: failedItem.name,
                          stockCode: failedItem.stockCode,
                          barcode: '',
                          error: failedItem.error
                        });
                      });
                    }
                    
                    console.log(`✅ [IMPORT] Batch ${batchIndex + 1} tamamlandı: ${result.successCount} başarılı, ${result.failedCount} başarısız`);
                    
                  } catch (error: any) {
                    console.error(`❌ [IMPORT] Batch ${batchIndex + 1} hatası:`, error);
                    
                    // Batch hatasında tüm batch'i başarısız say
                    failedCount += batch.length;
                    batch.forEach(item => {
                      failedProductsList.push({
                        name: item.name,
                        stockCode: item.stockCode,
                        barcode: item.barcode || '',
                        error: error?.response?.data?.message || error?.message || 'Batch kaydetme hatası'
                      });
                    });
                  }
                  
                  // Status güncelle
                  setSavingStatus(prev => ({
                    ...prev,
                    inventory: { 
                      ...prev.inventory, 
                      saved: savedCount,
                      failed: failedCount
                    }
                  }));
                  
                  // İlerleme göstergesini güncelle - batch'ler için %50-%100 arası
                  const batchProgress = 50 + ((batchIndex + 1) / totalBatches) * 50;
                  setLoadingProgress(Math.min(100, batchProgress));
                  
                  // Batch'ler arası kısa bekleme (sunucu yükünü azaltmak için)
                  if (batchIndex < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                  }
                }
              }
              
              // Çakışmaları state'e kaydet
              setBarcodeConflicts(conflicts);
              
              // ✅ Başarısız ürünleri state'e kaydet
              setFailedProducts(failedProductsList);
              
              // Stok kaydetme işlemi tamamlandı
              setSavingStatus(prev => ({
                ...prev,
                inventory: { 
                  ...prev.inventory, 
                  loading: false,
                  saved: savedCount,
                  failed: failedCount
                }
              }));
              
            } catch (error) {
              console.error('Stoklar kaydedilirken hata:', error);
              setSavingStatus(prev => ({
                ...prev,
                inventory: { ...prev.inventory, loading: false }
              }));
            }
          } else {
            setLoadingProgress(100); // Ürün yoksa direkt %100'e atla
          }
          
          // İşlem tamamlandı, realtime listener'ı tekrar aktifleştir
          toggleRealtimeListener(true);
          
          // İşlem tamamlandı
          setSuccess(true);
          setSavingData(false);
        } catch (error) {
          console.error('Veri kaydedilirken hata:', error);
          setError('Veriler kaydedilirken bir hata oluştu.');
          setSavingData(false);
          
          // Hata olsa da realtime listener'ı tekrar aktifleştir
          toggleRealtimeListener(true);
        }
      };
      
      reader.onerror = () => {
        setError('Dosya okuma hatası.');
        setSavingData(false);
        
        // Hata olsa da realtime listener'ı tekrar aktifleştir
        toggleRealtimeListener(true);
      };
      
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('İşlem sırasında hata:', error);
      setError('İşlem sırasında bir hata oluştu.');
      setSavingData(false);
      
      // Hata olsa da realtime listener'ı tekrar aktifleştir
      toggleRealtimeListener(true);
    }
  };
  
  // Barkod çakışma listesini aç/kapat
  const toggleBarcodeConflictsList = () => {
    setShowBarcodeConflicts(!showBarcodeConflicts);
  };
  
  // İptal onay modalını aç
  const handleOpenConfirmDialog = () => {
    // Eğer yükleme işlemi devam ediyorsa onay modalını göster
    if (savingData) {
      setConfirmDialogOpen(true);
    } else {
      // Değilse direkt modalı kapat
      handleModalClose();
    }
  };
  
  // İptal onay modalını kapa (işleme devam et)
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };
  
  // İşlemi iptal et ve sonuç sayfasına git
  const handleConfirmCancel = () => {
    // İptal onay modalını kapat
    setConfirmDialogOpen(false);
    
    // İşlemi iptal et
    setIsCancelled(true);
    
    // İlerleme çubuğunu 100% yap
    setLoadingProgress(100);
    
    // İşlem tamamlandı olarak işaretleyelim
    setSavingData(false);
    setSuccess(true);
    
    // Sonuç sayfasına git (adım 3)
    setStep(3);
    
    // Realtime listener'ı tekrar aktifleştir
    toggleRealtimeListener(true);
  };
  
  // Modal'ı kapatma işlemi
  const handleModalClose = () => {
    // Eğer kaydetme/işlem devam ediyorsa, direkt kapatmayalım
    if (savingData) {
      handleOpenConfirmDialog();
      return;
    }
    
    // Realtime listener'ı tekrar aktifleştir
    toggleRealtimeListener(true);
    
    // Form durumunu sıfırla
    setFile(null);
    setFileName('');
    setStartRow(2);
    setSheetNames([]);
    setSelectedSheet('');
    setPreviewData([]);
    setMappingValid(false);
    setLoading(false);
    setError(null);
    setSuccess(false);
    setStep(1);
    setSavingData(false);
    setSkipEmptyBarcodes(false); // Varsayılan: Otomatik barkod oluştur
    setAutoGeneratedBarcodes(0);
    setLoadingProgress(0);
    setCanBeNegative(true); // YENİ: State'i sıfırla
    setValidationResult(null); // ✅ Doğrulama sonuçlarını sıfırla
    
    // Eşleştirmeleri sıfırla
    const resetMappings = columnMappings.map(mapping => ({
      ...mapping,
      selected: '',
      // Para birimi için custom değerleri orijinal hallerine getir
      customValue: mapping.fieldName === 'purchaseCurrency' || mapping.fieldName === 'saleCurrency' 
        ? (currencies.length > 0 ? currencies[0].code : 'TRY')
        : mapping.fieldName === 'sctStatus' 
          ? null 
          : null
    }));
    setColumnMappings(resetMappings);
    
    // Modal'ı kapat
    onClose();
  };
  
  // Örnek Excel indirme fonksiyonu
  const handleDownloadSample = () => {
    // Örnek verileri oluştur
    const sampleData = [
      ['Ürün Adı', 'Barkod', 'Stok Kodu', 'Ana Kategori', 'Alt Kategori', 'Alt Kategori 2', 'Miktar', 'Birim', 'Alış Fiyatı', 'Satış Fiyatı', 'KDV Oranı', 'Açıklama'],
      ['Kalem', '8690123456789', 'KL1234', 'Kırtasiye', 'Yazı Gereçleri', 'Tükenmez', 100, 'adet', 2.5, 5, 18, 'Siyah tükenmez kalem'],
      ['Defter', '8690123456790', 'DF5678', 'Kırtasiye', 'Defterler', '', 50, 'adet', 10, 15, 8, 'A4 defter 100 sayfa'],
      ['Silgi', '8690123456791', 'SL9012', 'Kırtasiye', '', '', 200, 'adet', 1, 2, 18, 'Beyaz silgi'],
    ];
    
    // Worksheet oluştur
    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    
    // Workbook oluştur
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stok Örnek');
    
    // Excel dosyasını indir
    XLSX.writeFile(workbook, 'stok_aktarma_ornegi.xlsx');
  };
  
  // ✅ YENİ: Başarısız ürün ve resimlerin logunu Excel'e aktar
  const handleDownloadErrorLog = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // 1. Başarısız Ürünler Sayfası
      if (failedProducts.length > 0) {
        const productData = [
          ['Ürün Adı', 'Stok Kodu', 'Barkod', 'Hata Nedeni'],
          ...failedProducts.map(p => [p.name, p.stockCode, p.barcode, p.error])
        ];
        const productSheet = XLSX.utils.aoa_to_sheet(productData);
        XLSX.utils.book_append_sheet(workbook, productSheet, 'Başarısız Ürünler');
      }
      
      // 2. Başarısız Resimler Sayfası
      if (failedImages.length > 0) {
        const imageData = [
          ['Ürün Adı', 'Resim URL', 'Hata Nedeni'],
          ...failedImages.map(img => [img.productName, img.imageUrl, img.error])
        ];
        const imageSheet = XLSX.utils.aoa_to_sheet(imageData);
        XLSX.utils.book_append_sheet(workbook, imageSheet, 'Başarısız Resimler');
      }
      
      // 3. Barkod Çakışmaları Sayfası (opsiyonel)
      if (barcodeConflicts.length > 0) {
        const conflictData = [
          ['Import Ürün Adı', 'Barkod', 'Çakışan Ürün Adı', 'Çakışan Stok Kodu'],
          ...barcodeConflicts.map(c => [
            c.importItem.name || '',
            c.barcode,
            c.existingItem.name,
            c.existingItem.stockCode
          ])
        ];
        const conflictSheet = XLSX.utils.aoa_to_sheet(conflictData);
        XLSX.utils.book_append_sheet(workbook, conflictSheet, 'Barkod Çakışmaları');
      }
      
      // Excel dosyasını indir
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      XLSX.writeFile(workbook, `hata_logu_${timestamp}.xlsx`);
      
      console.log('✅ [IMPORT] Hata logu Excel\'e aktarıldı');
    } catch (error) {
      console.error('❌ [IMPORT] Hata logu oluşturulurken hata:', error);
    }
  };
  
  // ✅ YENİ: Çakışan ve duplike ürünleri karşılaştırmalı Excel'e aktar
  const handleDownloadConflictsExcel = () => {
    if (!validationResult || (validationResult.dbConflicts.length === 0 && validationResult.excelDuplicates.length === 0)) {
      return;
    }
    
    try {
      const workbook = XLSX.utils.book_new();
      
      // 1. VERİTABANI ÇAKIŞMALARI SAYFASI (varsa)
      if (validationResult.dbConflicts.length > 0) {
        const conflictData: any[][] = [
          // Başlık satırı
          [
            'Kaynak', 
            'Satır No', 
            'Ürün Adı', 
            'Stok Kodu', 
            'Barkod', 
            'Satış Fiyatı', 
            'Alış Fiyatı', 
            'Miktar', 
            'Birim', 
            'Kategori', 
            'Marka',
            'Çakışma Nedeni'
          ]
        ];
        
        // Her çakışan ürün için 2 satır ekle (Excel + Mevcut Stok)
        validationResult.dbConflicts.forEach((conflict, index) => {
          // 1. Excel'deki ürün bilgileri
          conflictData.push([
            '📥 Excel',
            conflict.excelItem.row,
            conflict.excelItem.name,
            conflict.excelItem.stockCode,
            conflict.excelItem.barcode,
            conflict.excelItem.salePrice,
            conflict.excelItem.purchasePrice,
            conflict.excelItem.quantity,
            conflict.excelItem.unit,
            conflict.excelItem.categoryName,
            conflict.excelItem.brandName,
            conflict.reason
          ]);
          
          // 2. Mevcut stoktaki ürün bilgileri
          conflictData.push([
            '📦 Mevcut Stok',
            '-',
            conflict.existingItem.name,
            conflict.existingItem.stockCode,
            conflict.existingItem.barcode,
            conflict.existingItem.salePrice,
            conflict.existingItem.purchasePrice,
            conflict.existingItem.quantity,
            conflict.existingItem.unit,
            conflict.existingItem.categoryName,
            conflict.existingItem.brandName,
            ''
          ]);
          
          // 3. Ayırıcı boş satır (grupları ayırmak için)
          if (index < validationResult.dbConflicts.length - 1) {
            conflictData.push(['', '', '', '', '', '', '', '', '', '', '', '']);
          }
        });
        
        const conflictSheet = XLSX.utils.aoa_to_sheet(conflictData);
        conflictSheet['!cols'] = [
          { wch: 15 },  // Kaynak
          { wch: 8 },   // Satır No
          { wch: 30 },  // Ürün Adı
          { wch: 15 },  // Stok Kodu
          { wch: 15 },  // Barkod
          { wch: 12 },  // Satış Fiyatı
          { wch: 12 },  // Alış Fiyatı
          { wch: 10 },  // Miktar
          { wch: 10 },  // Birim
          { wch: 15 },  // Kategori
          { wch: 15 },  // Marka
          { wch: 40 },  // Çakışma Nedeni
        ];
        XLSX.utils.book_append_sheet(workbook, conflictSheet, 'Veritabanı Çakışmaları');
      }
      
      // 2. EXCEL İÇİ DUPLİKASYONLAR SAYFASI (varsa) - Alt alta karşılaştırmalı
      if (validationResult.excelDuplicates.length > 0) {
        const duplicateData: any[][] = [
          // Başlık satırı
          [
            'Kaynak',
            'Satır No',
            'Ürün Adı',
            'Stok Kodu',
            'Barkod',
            'Satış Fiyatı',
            'Alış Fiyatı',
            'Miktar',
            'Birim',
            'Kategori',
            'Marka',
            'Açıklama'
          ]
        ];
        
        // Her duplikasyon için 2 satır ekle (Orijinal + Duplike)
        validationResult.excelDuplicates.forEach((dup, index) => {
          // 1. Orijinal ürün (ilk kaydedilen - yüklenecek olan)
          duplicateData.push([
            '✅ Orijinal (Yüklenecek)',
            dup.originalItem.row,
            dup.originalItem.name,
            dup.originalItem.stockCode,
            dup.originalItem.barcode,
            dup.originalItem.salePrice,
            dup.originalItem.purchasePrice,
            dup.originalItem.quantity,
            dup.originalItem.unit,
            dup.originalItem.categoryName,
            dup.originalItem.brandName,
            'Bu ürün yüklenecek'
          ]);
          
          // 2. Duplike ürün (atlanacak olan)
          duplicateData.push([
            '❌ Duplike (Atlanacak)',
            dup.duplicateItem.row,
            dup.duplicateItem.name,
            dup.duplicateItem.stockCode,
            dup.duplicateItem.barcode,
            dup.duplicateItem.salePrice,
            dup.duplicateItem.purchasePrice,
            dup.duplicateItem.quantity,
            dup.duplicateItem.unit,
            dup.duplicateItem.categoryName,
            dup.duplicateItem.brandName,
            `Stok kodu veya barkod Satır ${dup.originalItem.row} ile aynı`
          ]);
          
          // 3. Ayırıcı boş satır
          if (index < validationResult.excelDuplicates.length - 1) {
            duplicateData.push(['', '', '', '', '', '', '', '', '', '', '', '']);
          }
        });
        
        const duplicateSheet = XLSX.utils.aoa_to_sheet(duplicateData);
        duplicateSheet['!cols'] = [
          { wch: 22 },  // Kaynak
          { wch: 10 },  // Satır No
          { wch: 30 },  // Ürün Adı
          { wch: 15 },  // Stok Kodu
          { wch: 15 },  // Barkod
          { wch: 12 },  // Satış Fiyatı
          { wch: 12 },  // Alış Fiyatı
          { wch: 10 },  // Miktar
          { wch: 10 },  // Birim
          { wch: 15 },  // Kategori
          { wch: 15 },  // Marka
          { wch: 40 },  // Açıklama
        ];
        XLSX.utils.book_append_sheet(workbook, duplicateSheet, 'Excel İçi Duplikasyonlar');
      }
      
      // 3. ÖZET SAYFASI
      const summaryData = [
        ['📊 Doğrulama Sonuç Raporu'],
        [''],
        ['Rapor Tarihi:', new Date().toLocaleString('tr-TR')],
        [''],
        ['📈 İstatistikler:'],
        ['Toplam Ürün:', validationResult.totalItems],
        ['Yüklenebilir Ürün:', validationResult.validItems],
        ['Hatalı Ürün:', validationResult.invalidItems],
        [''],
        ['📋 Hata Detayları:'],
        ['Veritabanı Çakışmaları:', validationResult.dbConflicts.length],
        ['Excel İçi Duplikasyonlar:', validationResult.excelDuplicates.length],
        ['Eksik Zorunlu Alanlar:', validationResult.emptyRequired.length],
        [''],
        ['📝 Açıklamalar:'],
        [''],
        ['Veritabanı Çakışmaları:'],
        ['- Excel\'deki ürünün stok kodu veya barkodu sistemde zaten kayıtlı'],
        ['- Her çakışma için Excel\'deki ve mevcut stoktaki bilgiler alt alta gösterilir'],
        [''],
        ['Excel İçi Duplikasyonlar:'],
        ['- Aynı Excel dosyasında birden fazla satırda aynı stok kodu veya barkod var'],
        ['- Bu ürünlerden sadece ilki yüklenebilir, diğerleri atlanır'],
      ];
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      summarySheet['!cols'] = [{ wch: 30 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Özet');
      
      // Excel dosyasını indir
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      XLSX.writeFile(workbook, `dogrulama_sonucu_${timestamp}.xlsx`);
      
      console.log('✅ [VALIDATION] Doğrulama sonuçları Excel\'e aktarıldı');
    } catch (error) {
      console.error('❌ [VALIDATION] Doğrulama sonuçları Excel\'e aktarılırken hata:', error);
    }
  };
  
  // Modal açıldığında realtime listener'ı kapat
  useEffect(() => {
    if (open) {
      toggleRealtimeListener(false);
    }
    
    // Modal kapandığında realtime listener'ı aç
    return () => {
      toggleRealtimeListener(true);
    };
  }, [open]);
  
  return (
    <>
      <Dialog
        open={open}
        onClose={handleOpenConfirmDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6">{t('stock.import.title')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GuideButton guideKey="inventory.import" color="inherit" />
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleOpenConfirmDialog}
              aria-label={t('stock.common.close')}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Adımlar */}
          <Box sx={{ mt: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: step >= 1 ? 'primary.main' : 'grey.400', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  1
                </Box>
                <Typography variant="body1" color={step >= 1 ? 'text.primary' : 'text.secondary'} sx={{ ml: 1 }}>
                  {t('stock.import.steps.mapping')}
                </Typography>
              </Box>
              <Box sx={{ borderTop: '2px dashed', borderColor: step >= 2 ? 'primary.main' : 'grey.400', width: 60, mt: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: step >= 2 ? 'primary.main' : 'grey.400', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  2
                </Box>
                <Typography variant="body1" color={step >= 2 ? 'text.primary' : 'text.secondary'} sx={{ ml: 1 }}>
                  {t('stock.import.steps.preview')}
                </Typography>
              </Box>
              <Box sx={{ borderTop: '2px dashed', borderColor: step >= 3 ? 'primary.main' : 'grey.400', width: 60, mt: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: step >= 3 ? 'primary.main' : 'grey.400', 
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  3
                </Box>
                <Typography variant="body1" color={step >= 3 ? 'text.primary' : 'text.secondary'} sx={{ ml: 1 }}>
                  {t('stock.import.steps.result')}
                </Typography>
              </Box>
            </Box>
            <Divider />
          </Box>
          
          {/* Hata Mesajı */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {/* Başarı Mesajı */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('stock.import.success')}
            </Alert>
          )}
          
          {/* Adım 1: Veri Eşleştirme */}
          {step === 1 && (
            <>
              {/* Merkez Depo Bilgisi - En Üstte */}
              {warehouses.length > 0 && warehouses.find(w => 
                w.name.toLowerCase().includes('merkez') ||
                w.name.toLowerCase() === 'merkez depo' ||
                w.name.toLowerCase() === 'ana depo'
              ) && (
                <Alert severity="info" icon={<WarehouseIcon />} sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold">
                    📦 {t('stock.import.centerWarehouseInfo', { warehouse: warehouses.find(w => 
                      w.name.toLowerCase().includes('merkez') ||
                      w.name.toLowerCase() === 'merkez depo' ||
                      w.name.toLowerCase() === 'ana depo'
                    )?.name })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('stock.import.centerWarehouseHint')}
                  </Typography>
                </Alert>
              )}
              
              {/* Örnek Excel İndir Butonu */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSample}
                  size="large"
                >
                  {t('stock.import.downloadSample')}
                </Button>
              </Box>
              
              {/* İçe Aktarma Ayarları */}
              <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                  ⚙️ {t('stock.import.settingsTitle')}
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Başlık/boş satırları filtrele ve Stok eksiye düşebilir - Aynı satırda */}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enableEmptyRowFilter}
                          onChange={(e) => setEnableEmptyRowFilter(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t('stock.import.settings.filterHeaderEmpty')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={canBeNegative}
                          onChange={(e) => setCanBeNegative(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t('stock.import.settings.canBeNegative')}
                    />
                  </Grid>
                  
                  {/* Veriler kaçıncı satırdan başlıyor ve Minimum dolu sütun sayısı - Aynı satırda */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('stock.import.settings.startRow')}
                      type="number"
                      size="small"
                      value={startRow}
                      onChange={handleStartRowChange}
                      InputProps={{
                        inputProps: { min: 1 }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    {enableEmptyRowFilter && (
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t('stock.import.settings.minFilledColumns')}
                        value={minRequiredColumns}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value > 0) {
                            setMinRequiredColumns(value);
                          }
                        }}
                        inputProps={{
                          min: 1,
                          max: 10,
                          step: 1
                        }}
                      />
                    )}
                  </Grid>
                  
                  {/* Boş barkod işleme - Tam genişlik */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={skipEmptyBarcodes}
                          onChange={(e) => setSkipEmptyBarcodes(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={t('stock.import.settings.skipEmptyBarcode')}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Zorunlu alanlar bildirimi */}
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>{t('stock.import.requiredFieldsInfo')}</strong>
                </Typography>
              </Alert>
              
              {/* Sütun eşleştirmeleri */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      {t('stock.import.columnMappings')}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {columnMappings.map((mapping, index) => (
                        <Grid item xs={12} sm={6} md={4} key={mapping.fieldName}>
                          {mapping.showColumnSelect ? (
                            <TextField
                              variant="outlined"
                              label={`${mapping.label} ${mapping.required ? '*' : ''}`}
                              size="small"
                              value={mapping.selected}
                              onChange={(e) => handleColumnInputChange(index, e.target.value)}
                              fullWidth
                            />
                          ) : mapping.fieldName === 'purchaseCurrency' || mapping.fieldName === 'saleCurrency' ? (
                            <FormControl fullWidth size="small">
                              <InputLabel id={`custom-${mapping.fieldName}-label`}>
                                {mapping.label} {mapping.required && '*'}
                              </InputLabel>
                              <Select
                                labelId={`custom-${mapping.fieldName}-label`}
                                value={mapping.customValue || ''}
                                onChange={(e) => handleCustomValueChange(index, e.target.value)}
                                label={`${mapping.label} ${mapping.required ? '*' : ''}`}
                              >
                                {currencies.map(currency => (
                                  <MenuItem key={currency.code} value={currency.code}>
                                    {currency.code}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : mapping.fieldName === 'sctStatus' ? (
                            <FormControl fullWidth size="small">
                              <InputLabel id={`custom-${mapping.fieldName}-label`}>
                                {mapping.label}
                              </InputLabel>
                              <Select
                                labelId={`custom-${mapping.fieldName}-label`}
                                value={mapping.customValue || ''}
                                onChange={(e) => handleCustomValueChange(index, e.target.value)}
                                label={mapping.label}
                              >
                                <MenuItem value="included">{t('stock.import.sctIncluded')}</MenuItem>
                                <MenuItem value="excluded">{t('stock.import.sctExcluded')}</MenuItem>
                              </Select>
                            </FormControl>
                          ) : null}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
                
                {/* Excel Yükleme */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <input
                    id="excel-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  
                  <ImportArea 
                    onClick={handleClickImport}
                    sx={{ opacity: requiredFieldsMissing ? 0.7 : 1 }}
                  >
                    <UploadIcon fontSize="large" color="primary" />
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      {t('stock.import.uploadExcel')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('stock.import.uploadHint')}
                    </Typography>
                    {fileName && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                        {t('stock.import.selectedFile', { fileName })}
                      </Typography>
                    )}
                  </ImportArea>
                  
                  {requiredFieldsMissing && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {t('stock.import.requiredMappingsHint')}
                    </Typography>
                  )}
                </Grid>
                
                {/* Çalışma Sayfası Seçimi */}
                {file && sheetNames.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="sheet-select-label">{t('stock.import.sheet')}</InputLabel>
                      <Select
                        labelId="sheet-select-label"
                        value={selectedSheet}
                        onChange={handleSheetChange}
                        label={t('stock.import.sheet')}
                      >
                        {sheetNames.map(sheet => (
                          <MenuItem key={sheet} value={sheet}>
                            {sheet}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </>
          )}
          
          {/* Adım 2: Veri Önizleme */}
          {step === 2 && (
            <>
              {/* ✅ DOĞRULAMA SONUÇLARI PANELİ */}
              {validationResult && (
                <Paper 
                  elevation={0} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 3, 
                    bgcolor: validationResult.invalidItems > 0 ? 'warning.light' : 'success.light',
                    borderColor: validationResult.invalidItems > 0 ? 'warning.main' : 'success.main'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {validationResult.loading ? (
                        <>
                          <CircularProgress size={20} />
                          Doğrulama taraması yapılıyor...
                        </>
                      ) : (
                        <>
                          📊 Hızlı Doğrulama Sonucu
                        </>
                      )}
                    </Typography>
                    
                    {/* ✅ Hatalı ürünleri Excel'e indir butonu - çakışma veya duplikasyon varsa göster */}
                    {!validationResult.loading && (validationResult.dbConflicts.length > 0 || validationResult.excelDuplicates.length > 0) && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadConflictsExcel}
                        sx={{ 
                          bgcolor: 'error.main',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        Sonuç Excel'i İndir ({validationResult.dbConflicts.length + validationResult.excelDuplicates.length} hata)
                      </Button>
                    )}
                  </Box>
                  
                  {!validationResult.loading && (
                    <>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper' }}>
                            <Typography variant="h4" color="text.primary">
                              {validationResult.totalItems}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Toplam Ürün
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.main' }}>
                            <Typography variant="h4" color="white">
                              {validationResult.validItems}
                            </Typography>
                            <Typography variant="caption" color="white">
                              ✓ Yüklenebilir
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: validationResult.invalidItems > 0 ? 'error.main' : 'grey.300' }}>
                            <Typography variant="h4" color={validationResult.invalidItems > 0 ? 'white' : 'text.secondary'}>
                              {validationResult.invalidItems}
                            </Typography>
                            <Typography variant="caption" color={validationResult.invalidItems > 0 ? 'white' : 'text.secondary'}>
                              ✗ Hatalı
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                      
                      {/* Hata Detayları */}
                      {validationResult.invalidItems > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {/* Veritabanı Çakışmaları */}
                          {validationResult.dbConflicts.length > 0 && (
                            <Collapse in={true}>
                              <Alert severity="error" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  ⚠️ Veritabanı Çakışmaları ({validationResult.dbConflicts.length})
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Bu ürünler veritabanında zaten mevcut (stok kodu veya barkod çakışması):
                                </Typography>
                                <Box sx={{ maxHeight: 100, overflow: 'auto', mt: 1 }}>
                                  {validationResult.dbConflicts.slice(0, 10).map((conflict, idx) => (
                                    <Chip 
                                      key={idx} 
                                      size="small" 
                                      label={`${conflict.name} (${conflict.stockCode || conflict.barcode})`}
                                      sx={{ m: 0.25 }}
                                    />
                                  ))}
                                  {validationResult.dbConflicts.length > 10 && (
                                    <Chip 
                                      size="small" 
                                      label={`+${validationResult.dbConflicts.length - 10} daha...`}
                                      color="default"
                                      sx={{ m: 0.25 }}
                                    />
                                  )}
                                </Box>
                              </Alert>
                            </Collapse>
                          )}
                          
                          {/* Excel İçi Duplikasyonlar */}
                          {validationResult.excelDuplicates.length > 0 && (
                            <Collapse in={true}>
                              <Alert severity="warning" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  🔄 Excel İçi Duplikasyonlar ({validationResult.excelDuplicates.length})
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Bu ürünler Excel dosyasında birden fazla kez var (aynı stok kodu veya barkod):
                                </Typography>
                                <Box sx={{ maxHeight: 100, overflow: 'auto', mt: 1 }}>
                                  {validationResult.excelDuplicates.slice(0, 10).map((dup, idx) => (
                                    <Chip 
                                      key={idx} 
                                      size="small" 
                                      label={`Satır ${dup.row}: ${dup.name} (${dup.stockCode || dup.barcode}) = "${dup.duplicateOf}" ile aynı`}
                                      color="warning"
                                      sx={{ m: 0.25 }}
                                    />
                                  ))}
                                  {validationResult.excelDuplicates.length > 10 && (
                                    <Chip 
                                      size="small" 
                                      label={`+${validationResult.excelDuplicates.length - 10} daha...`}
                                      color="default"
                                      sx={{ m: 0.25 }}
                                    />
                                  )}
                                </Box>
                              </Alert>
                            </Collapse>
                          )}
                          
                          {/* Boş Zorunlu Alanlar */}
                          {validationResult.emptyRequired.length > 0 && (
                            <Collapse in={true}>
                              <Alert severity="info" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  📝 Eksik Zorunlu Alanlar ({validationResult.emptyRequired.length})
                                </Typography>
                                <Typography variant="caption" component="div">
                                  Bu satırlarda zorunlu alanlar boş:
                                </Typography>
                                <Box sx={{ maxHeight: 60, overflow: 'auto', mt: 1 }}>
                                  {validationResult.emptyRequired.slice(0, 5).map((empty, idx) => (
                                    <Chip 
                                      key={idx} 
                                      size="small" 
                                      label={`Satır ${empty.row}: ${empty.field} boş`}
                                      color="info"
                                      sx={{ m: 0.25 }}
                                    />
                                  ))}
                                  {validationResult.emptyRequired.length > 5 && (
                                    <Chip 
                                      size="small" 
                                      label={`+${validationResult.emptyRequired.length - 5} daha...`}
                                      color="default"
                                      sx={{ m: 0.25 }}
                                    />
                                  )}
                                </Box>
                              </Alert>
                            </Collapse>
                          )}
                        </Box>
                      )}
                      
                      {/* Başarı Mesajı */}
                      {validationResult.invalidItems === 0 && (
                        <Alert severity="success">
                          ✅ Tüm ürünler doğrulamadan geçti! Yüklemeye hazır.
                        </Alert>
                      )}
                    </>
                  )}
                </Paper>
              )}
              
              <Typography variant="body1" gutterBottom>
                Excel verilerinizin önizlemesi aşağıda gösteriliyor. Doğruluğunu kontrol edin.
              </Typography>
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                (İlk 10 satır gösteriliyor)
                {skippedRows > 0 && ` - ${skippedRows} başlık/boş satır filtrelendi`}
              </Typography>
              
              {previewData.length === 0 ? (
                <Alert severity="warning">
                  Gösterilecek önizleme verisi bulunamadı.
                  {enableEmptyRowFilter && skippedRows > 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {skippedRows} satır başlık/boş satır olarak algılanıp filtrelendi. 
                      Eğer bu bir hata ise, "Başlık/boş satırları filtrele" ayarını kapatın veya "Minimum dolu sütun sayısı" değerini azaltın.
                    </Typography>
                  )}
                </Alert>
              ) : (
                <ScrollableContent>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {columnMappings
                            .filter(mapping => mapping.selected !== '' || mapping.customValue !== null)
                            .map(mapping => (
                              <TableCell key={mapping.fieldName}>
                                {mapping.label}
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.map((item, index) => (
                          <TableRow key={index}>
                            {columnMappings
                              .filter(mapping => mapping.selected !== '' || mapping.customValue !== null)
                              .map(mapping => (
                                <TableCell key={`${index}-${mapping.fieldName}`}>
                                  {item[mapping.fieldName] !== undefined ? item[mapping.fieldName] : '-'}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </ScrollableContent>
              )}
            </>
          )}
          
          {/* İçe Aktarma Sonucu - Adım 3 */}
          {step === 3 && (
            <Box sx={{ height: 'auto', width: '100%', p: 2 }}>
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : success ? (
                <>
                  <Alert severity={isCancelled ? "warning" : "success"} sx={{ mb: 2 }}>
                    {isCancelled 
                      ? "İşlem iptal edildi. Şu ana kadar aktarılan veriler kaydedildi."
                      : "Veriler başarıyla içe aktarıldı!"}
                    {barcodeConflicts.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Not: {barcodeConflicts.length} ürün barkod çakışması nedeniyle işlenmedi.
                      </Typography>
                    )}
                    {autoGeneratedBarcodes > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Not: {autoGeneratedBarcodes} ürüne otomatik barkod atandı.
                      </Typography>
                    )}
                  </Alert>
                  
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      İçe Aktarma Özeti
                    </Typography>
                    
                    {/* Kategori Durumu */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">
                        Kategoriler: {savingStatus.categories.total > 0 ? (
                          <>
                            {savingStatus.categories.saved}/{savingStatus.categories.total}
                            {savingStatus.categories.loading ? ' (Kaydediliyor...)' : ' (Tamamlandı)'}
                          </>
                        ) : 'Yeni kategori yok'}
                      </Typography>
                      
                      {savingStatus.categories.total > 0 && (
                        <LinearProgress 
                          variant={savingStatus.categories.loading ? "indeterminate" : "determinate"} 
                          value={savingStatus.categories.loading ? undefined : (savingStatus.categories.saved / savingStatus.categories.total) * 100} 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                    
                    {/* Marka Durumu */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">
                        Markalar: {savingStatus.brands.total > 0 ? (
                          <>
                            {savingStatus.brands.saved}/{savingStatus.brands.total}
                            {savingStatus.brands.loading ? ' (Kaydediliyor...)' : ' (Tamamlandı)'}
                          </>
                        ) : 'Yeni marka yok'}
                      </Typography>
                      
                      {savingStatus.brands.total > 0 && (
                        <LinearProgress 
                          variant={savingStatus.brands.loading ? "indeterminate" : "determinate"} 
                          value={savingStatus.brands.loading ? undefined : (savingStatus.brands.saved / savingStatus.brands.total) * 100} 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                    
                    {/* ✅ YENİ: Resim Durumu */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">
                        Resimler: {savingStatus.images.total > 0 ? (
                          <>
                            {savingStatus.images.saved}/{savingStatus.images.total}
                            {savingStatus.images.loading ? ' (İşleniyor...)' : ' (Tamamlandı)'}
                            {savingStatus.images.failed > 0 && (
                              <Chip 
                                color="warning" 
                                size="small" 
                                label={`${savingStatus.images.failed} resim işlenemedi`} 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </>
                        ) : 'Resim yok'}
                      </Typography>
                      
                      {savingStatus.images.total > 0 && (
                        <LinearProgress 
                          variant={savingStatus.images.loading ? "indeterminate" : "determinate"} 
                          value={savingStatus.images.loading ? undefined : (savingStatus.images.saved / savingStatus.images.total) * 100} 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                    
                    {/* Stok Durumu */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">
                        Stok Ürünleri: {savingStatus.inventory.total > 0 ? (
                          <>
                            {savingStatus.inventory.saved}/{savingStatus.inventory.total}
                            {savingStatus.inventory.loading ? ' (Kaydediliyor...)' : ' (Tamamlandı)'}
                            {savingStatus.inventory.failed > 0 && (
                              <Chip 
                                color="error" 
                                size="small" 
                                label={`${savingStatus.inventory.failed} ürün işlenemedi`} 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </>
                        ) : 'Stok ürünü yok'}
                      </Typography>
                      
                      {savingStatus.inventory.total > 0 && (
                        <LinearProgress 
                          variant={savingStatus.inventory.loading ? "indeterminate" : "determinate"} 
                          value={savingStatus.inventory.loading ? undefined : (savingStatus.inventory.saved / savingStatus.inventory.total) * 100} 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                    
                    {/* Barkod Çakışmaları */}
                    {barcodeConflicts.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Button 
                          variant="outlined" 
                          color="error"
                          onClick={toggleBarcodeConflictsList}
                          endIcon={showBarcodeConflicts ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          sx={{ mb: 1 }}
                        >
                          Barkod Çakışmaları ({barcodeConflicts.length})
                        </Button>
                        
                        <Collapse in={showBarcodeConflicts}>
                          <Paper variant="outlined" sx={{ mt: 1, p: 0, maxHeight: 300, overflow: 'auto' }}>
                            <List dense disablePadding>
                              {barcodeConflicts.map((conflict, index) => (
                                <ListItem key={index} divider={index < barcodeConflicts.length - 1}>
                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="subtitle2">
                                          {conflict.importItem.name}
                                        </Typography>
                                        <Chip 
                                          size="small" 
                                          label={conflict.barcode} 
                                          color="default"
                                          variant="outlined" 
                                          sx={{ ml: 1 }} 
                                        />
                                        <Tooltip title="Bu barkod zaten kullanımda olduğu için ürün aktarılamadı">
                                          <InfoIcon fontSize="small" color="error" sx={{ ml: 1, cursor: 'help' }} />
                                        </Tooltip>
                                      </Box>
                                    }
                                    secondary={
                                      <Box sx={{ mt: 1 }}>
                                        <Typography variant="caption" display="block">
                                          <strong>Çakışan Ürün:</strong> {conflict.existingItem.name} 
                                          {conflict.existingItem.stockCode && ` (${conflict.existingItem.stockCode})`}
                                        </Typography>
                                        
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                          {conflict.existingItem.categoryName && (
                                            <Chip 
                                              size="small" 
                                              label={`Kategori: ${conflict.existingItem.categoryName}`} 
                                              variant="outlined" 
                                            />
                                          )}
                                          
                                          {conflict.existingItem.warehouseName && (
                                            <Chip 
                                              size="small" 
                                              label={`Depo: ${conflict.existingItem.warehouseName}`} 
                                              variant="outlined" 
                                            />
                                          )}
                                          
                                          {conflict.existingItem.brandName && (
                                            <Chip 
                                              size="small" 
                                              label={`Marka: ${conflict.existingItem.brandName}`} 
                                              variant="outlined" 
                                            />
                                          )}
                                          
                                          {conflict.existingItem.quantity !== undefined && (
                                            <Chip 
                                              size="small" 
                                              label={`Miktar: ${conflict.existingItem.quantity} ${conflict.existingItem.unit || 'adet'}`} 
                                              variant="outlined" 
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Paper>
                        </Collapse>
                      </Box>
                    )}
                    
                    {/* ✅ YENİ: Hata Logu İndirme Butonu */}
                    {(failedProducts.length > 0 || failedImages.length > 0 || barcodeConflicts.length > 0) && (
                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                        <Button 
                          variant="contained" 
                          color="warning"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadErrorLog}
                          sx={{ minWidth: 200 }}
                        >
                          Hata Logunu İndir (Excel)
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2, display: 'flex', alignItems: 'center' }}>
                          {failedProducts.length > 0 && `${failedProducts.length} başarısız ürün`}
                          {failedProducts.length > 0 && failedImages.length > 0 && ' • '}
                          {failedImages.length > 0 && `${failedImages.length} başarısız resim`}
                          {(failedProducts.length > 0 || failedImages.length > 0) && barcodeConflicts.length > 0 && ' • '}
                          {barcodeConflicts.length > 0 && `${barcodeConflicts.length} barkod çakışması`}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                    Veriler işleniyor...
                  </Typography>
                  
                  {/* İlerleme çubuğu */}
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={loadingProgress} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      %{Math.round(loadingProgress)}
                    </Typography>
                  </Box>
                  
                  {/* Yükleme istatistikleri */}
                  <Box sx={{ width: '100%', mt: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Yükleme İstatistikleri:
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={3}>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          Kategoriler:
                        </Typography>
                        <Typography variant="body2">
                          {savingStatus.categories.saved}/{savingStatus.categories.total}
                          {savingStatus.categories.loading && ' (İşleniyor...)'}
                        </Typography>
                        {savingStatus.categories.total > 0 && (
                          <LinearProgress 
                            variant={savingStatus.categories.loading ? "indeterminate" : "determinate"} 
                            value={savingStatus.categories.loading ? undefined : (savingStatus.categories.saved / savingStatus.categories.total) * 100} 
                            sx={{ mt: 1, height: 6 }} 
                          />
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          Markalar:
                        </Typography>
                        <Typography variant="body2">
                          {savingStatus.brands.saved}/{savingStatus.brands.total}
                          {savingStatus.brands.loading && ' (İşleniyor...)'}
                        </Typography>
                        {savingStatus.brands.total > 0 && (
                          <LinearProgress 
                            variant={savingStatus.brands.loading ? "indeterminate" : "determinate"} 
                            value={savingStatus.brands.loading ? undefined : (savingStatus.brands.saved / savingStatus.brands.total) * 100} 
                            sx={{ mt: 1, height: 6 }} 
                          />
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          Resimler:
                        </Typography>
                        <Typography variant="body2">
                          {savingStatus.images.saved}/{savingStatus.images.total}
                          {savingStatus.images.loading && ' (İşleniyor...)'}
                          {savingStatus.images.failed > 0 && (
                            <Chip 
                              color="warning" 
                              size="small" 
                              label={`${savingStatus.images.failed} hata`} 
                              sx={{ ml: 0.5 }} 
                            />
                          )}
                        </Typography>
                        {savingStatus.images.total > 0 && (
                          <LinearProgress 
                            variant={savingStatus.images.loading ? "indeterminate" : "determinate"} 
                            value={savingStatus.images.loading ? undefined : (savingStatus.images.saved / savingStatus.images.total) * 100} 
                            sx={{ mt: 1, height: 6 }} 
                          />
                        )}
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="body2" gutterBottom fontWeight="medium">
                          Stok Ürünleri:
                        </Typography>
                        <Typography variant="body2">
                          {savingStatus.inventory.saved}/{savingStatus.inventory.total}
                          {savingStatus.inventory.loading && ' (İşleniyor...)'}
                          {savingStatus.inventory.failed > 0 && (
                            <Chip 
                              color="error" 
                              size="small" 
                              label={`${savingStatus.inventory.failed} hata`} 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                        {savingStatus.inventory.total > 0 && (
                          <LinearProgress 
                            variant={savingStatus.inventory.loading ? "indeterminate" : "determinate"} 
                            value={savingStatus.inventory.loading ? undefined : (savingStatus.inventory.saved / savingStatus.inventory.total) * 100} 
                            sx={{ mt: 1, height: 6 }} 
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* İptal Etme Butonu */}
                  <Box sx={{ mt: 3 }}>
                    <Button 
                      variant="outlined" 
                      color="error"
                      onClick={handleOpenConfirmDialog}
                      startIcon={<CloseIcon />}
                    >
                      {t('stock.import.actions.cancelProcess')}
                    </Button>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={handleModalClose} 
                  color="primary"
                  disabled={savingData}
                >
                  {t('stock.common.close')}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {step === 1 && (
            <>
              <Button onClick={handleModalClose} variant="outlined">
                {t('stock.common.cancel')}
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
                disabled={!file || !selectedSheet || requiredFieldsMissing}
              >
                {t('stock.import.actions.preview')}
              </Button>
            </>
          )}
          
          {step === 2 && (
            <>
              <Button onClick={() => setStep(1)} variant="outlined">
                {t('stock.report.actions.back')}
              </Button>
              <Button 
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveData}
                disabled={previewData.length === 0 || savingData}
              >
                {savingData ? t('stock.import.actions.importing') : t('stock.import.actions.importData')}
              </Button>
            </>
          )}
          
          {step === 3 && (
            <Button onClick={handleModalClose} variant="contained">
              {t('stock.common.close')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* İptal Onay Modalı */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('stock.import.cancelDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('stock.import.cancelDialog.description')}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'warning.main' }}>
            {t('stock.import.cancelDialog.warning')}
          </DialogContentText>
          
          {/* Yükleme durumunu göster */}
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="body2" gutterBottom>
              {t('stock.import.cancelDialog.currentProgress')}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={loadingProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('stock.import.cancelDialog.completedPercent', { percent: Math.round(loadingProgress) })}
            </Typography>
          </Box>
          
          {/* Toplam istatistikler */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              {t('stock.import.cancelDialog.stats')}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">
                  {t('stock.import.cancelDialog.categories')}: 
                  {savingStatus.categories.saved}/{savingStatus.categories.total}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">
                  {t('stock.import.cancelDialog.brands')}: 
                  {savingStatus.brands.saved}/{savingStatus.brands.total}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" display="block">
                  {t('stock.import.cancelDialog.inventoryItems')}: 
                  {savingStatus.inventory.saved}/{savingStatus.inventory.total}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            {t('stock.import.cancelDialog.continue')}
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            {t('stock.import.cancelDialog.cancelAndShow')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InventoryImportModal;
export { toggleRealtimeListener, setRealtimeListenerToggle }; 